import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import IntegrationIndicator from './IntegrationIndicator';
import { API_BASE_URL } from '../config';

const ProjectBudgets = () => {
  const location = useLocation();
  // State for budgets
  const [budgets, setBudgets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // State for budget lines
  const [budgetLines, setBudgetLines] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  
  // State for Internal Change Orders
  const [internalChangeOrders, setInternalChangeOrders] = useState([]);
  const [icoLines, setIcoLines] = useState([]);
  
  // State for WIP data to get current budget amounts
  const [wipData, setWipData] = useState([]);
  
  // State for external change order lines to show impacts
  const [externalChangeOrderLines, setExternalChangeOrderLines] = useState([]);
  
  // State for Accounting Periods
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [accountingPeriodDropdownOpen, setAccountingPeriodDropdownOpen] = useState(false);
  
  // Ref for CSV upload input
  const csvUploadInputRef = useRef(null);
  
  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showLinesForm, setShowLinesForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);

  const [selectedBudget, setSelectedBudget] = useState(null);
  
  // Form data
  // Initialize form data with project context from URL if available
  const getInitialFormData = () => {
    const searchParams = new URLSearchParams(location.search);
    const projectVuid = searchParams.get('project');
    
    return {
      project_vuid: projectVuid || '',
      accounting_period_vuid: '',
      description: '',
      budget_type: 'original',
      budget_date: '',
      finalized: false
    };
  };

  // Form data state
  const [formData, setFormData] = useState(getInitialFormData());
  

  const [lineFormData, setLineFormData] = useState({
    budget_vuid: '',
    cost_code_vuid: '',
    cost_type_vuid: '',
    budget_amount: '',
    notes: ''
  });
  
  // Filter and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [budgetsPerPage] = useState(10);
  const [filteredBudgets, setFilteredBudgets] = useState([]);
  
  // Dropdown states
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [costCodeDropdownOpen, setCostCodeDropdownOpen] = useState(false);
  const [costTypeDropdownOpen, setCostTypeDropdownOpen] = useState(false);
  
  // Excel-style form states
  const [filteredCostCodes, setFilteredCostCodes] = useState([]);
  const [filteredCostTypes, setFilteredCostTypes] = useState([]);
  const [newLineCostCodeDropdownOpen, setNewLineCostCodeDropdownOpen] = useState(false);
  const [newLineCostTypeDropdownOpen, setNewLineCostTypeDropdownOpen] = useState(false);
  const [newLineData, setNewLineData] = useState({
    cost_code_vuid: '',
    cost_type_vuid: '',
    budget_amount: '',
    notes: ''
  });

  // Integration state
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [budgetForIntegration, setBudgetForIntegration] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [retrieveIntegration, setRetrieveIntegration] = useState(null);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [progressErrors, setProgressErrors] = useState([]);
  const [progressComplete, setProgressComplete] = useState(false);
  
  // UI state
  const [showCsvHelp, setShowCsvHelp] = useState(false);
  
  // Retrieved budgets state
  const [showRetrievedBudgetsModal, setShowRetrievedBudgetsModal] = useState(false);
  const [retrievedBudgetsData, setRetrievedBudgetsData] = useState([]);
  const [selectedBudgetsToImport, setSelectedBudgetsToImport] = useState([]);
  
  // Finalization state
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);

  // Project-specific cost codes state
  const [projectSettings, setProjectSettings] = useState(null);
  const [useProjectCostCodes, setUseProjectCostCodes] = useState(false);
  const [projectCostCodes, setProjectCostCodes] = useState([]);
  
  // Saving state to prevent duplicate saves
  const [saving, setSaving] = useState(false);
  

  


  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Handle query parameters for project filtering and budget viewing
  useEffect(() => {
    const handleUrlParams = async () => {
      const searchParams = new URLSearchParams(location.search);
      const projectVuid = searchParams.get('project');
      const budgetVuid = searchParams.get('budget');
      
      // Only process URL parameters if projects are loaded
      if (projects.length === 0) {
        return;
      }
      
      if (projectVuid) {
        // Check if the project exists in the loaded projects
        const foundProject = projects.find(p => p.vuid === projectVuid);
        
        if (!foundProject) {
          return; // Don't proceed if project doesn't exist
        }
        
        // Pre-filter budgets for this project (only if filter is different)
        if (projectFilter !== projectVuid) {
          setProjectFilter(projectVuid);
        }
        
        // If there's also a create parameter, open the create form (only if not already open)
        if (searchParams.get('create') === 'true' && !showCreateForm) {
          initializeForm();
          setShowCreateForm(true);
        }
        
        // Only refetch data if we're not in create form mode (to avoid interrupting user input)
        if (!showCreateForm) {
          fetchData();
        }
        
        // Reset cost code toggle when project changes - will be auto-set by useEffect if project allows it
        // Don't reset here - let the useEffect handle it based on project settings
      }
      
      if (budgetVuid) {
        // Find and select the budget to view
        const budget = budgets.find(b => b.vuid === budgetVuid);
        if (budget && selectedBudget?.vuid !== budget.vuid) {
          setSelectedBudget(budget);
          // Budget lines are already in the budget object - extract them
          if (budget.budget_lines && budget.budget_lines.length > 0) {
            const linesWithBudgetVuid = budget.budget_lines.map(line => ({
              ...line,
              budget_vuid: budget.vuid
            }));
            setBudgetLines(linesWithBudgetVuid);
            console.log(`Budget lines loaded from budget object:`, linesWithBudgetVuid.length);
          } else {
            fetchBudgetLines(budget.vuid);
          }
        }
      }
    };

    handleUrlParams();
  }, [location.search]); // Only depend on location.search, not projects to avoid re-renders during form input

  // Reset CSV help accordion when changing budgets
  useEffect(() => {
    setShowCsvHelp(false);
  }, [selectedBudget]);

  // Handle navigation state for viewing budgets and creating for specific projects (legacy support)
  useEffect(() => {
    const handleLocationState = () => {
      if (window.location.state) {
        if (window.location.state.viewBudget) {
          // Find and select the budget to view
          const budget = window.location.state.viewBudget;
          setSelectedBudget(budget);
          // Budget lines are already in the budget object
          if (budget.budget_lines && budget.budget_lines.length > 0) {
            const linesWithBudgetVuid = budget.budget_lines.map(line => ({
              ...line,
              budget_vuid: budget.vuid
            }));
            setBudgetLines(linesWithBudgetVuid);
          } else {
            fetchBudgetLines(budget.vuid);
          }
          // Clear the state
          window.history.replaceState({}, document.title);
        } else if (window.location.state.createForProject) {
          // Pre-fill the form with project data and filter budgets
          const project = window.location.state.createForProject;
          
          // Get open accounting periods
          const openPeriods = accountingPeriods.filter(p => p.status === 'open');
          let defaultPeriodVuid = '';
          if (openPeriods.length === 1) {
            defaultPeriodVuid = openPeriods[0].vuid;
          }
          
          setFormData(prev => ({
            ...prev,
            project_vuid: project.vuid,
            accounting_period_vuid: defaultPeriodVuid,
            budget_date: new Date().toISOString().split('T')[0]
          }));
          setShowCreateForm(true);
          
          // Pre-filter budgets to show only this project's budgets
          setProjectFilter(project.vuid);
          
          // Clear the state
          window.history.replaceState({}, document.title);
        }
      }
    };

    handleLocationState();
  }, []);

  // Add click outside handlers for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setProjectDropdownOpen(false);
        setTypeDropdownOpen(false);
        setCostCodeDropdownOpen(false);
        setCostTypeDropdownOpen(false);
        setNewLineCostCodeDropdownOpen(false);
        setNewLineCostTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use hardcoded network URL for Safari compatibility
      const baseURL = 'http://localhost:5001';
      // Check if we have a project filter from URL params
      const searchParams = new URLSearchParams(location.search);
      const projectVuid = searchParams.get('project');
      
      // Build the budgets URL with project filter if available
      let budgetsUrl = `${baseURL}/api/project-budgets`;
      if (projectVuid) {
        budgetsUrl += `?project_vuid=${projectVuid}`;
      }
      
      const [budgetsRes, projectsRes, costCodesRes, costTypesRes, icosRes, accountingPeriodsRes, wipRes] = await Promise.allSettled([
        axios.get(budgetsUrl),
        axios.get(`${baseURL}/api/projects`),
        axios.get(`${baseURL}/api/costcodes`),
        axios.get(`${baseURL}/api/costtypes`),
        axios.get(`${baseURL}/api/internal-change-orders`).catch(() => ({ data: [] })),  // Handle 404 gracefully
        axios.get(`${baseURL}/api/accounting-periods/open`),
        axios.get(`${baseURL}/api/wip`)
      ]);
      
      // Handle responses with error handling
      setBudgets(budgetsRes.status === 'fulfilled' ? budgetsRes.value.data : []);
      setProjects(projectsRes.status === 'fulfilled' ? projectsRes.value.data : []);
      setCostCodes(costCodesRes.status === 'fulfilled' ? costCodesRes.value.data : []);
      setCostTypes(costTypesRes.status === 'fulfilled' ? costTypesRes.value.data : []);
      setInternalChangeOrders(icosRes.status === 'fulfilled' ? (icosRes.value.data || []) : []);
      setAccountingPeriods(accountingPeriodsRes.status === 'fulfilled' ? accountingPeriodsRes.value.data : []);
      setWipData(wipRes.status === 'fulfilled' ? wipRes.value.data : []);
      setFilteredBudgets(budgetsRes.status === 'fulfilled' ? budgetsRes.value.data : []);
      setFilteredCostCodes(costCodesRes.status === 'fulfilled' ? costCodesRes.value.data.filter(c => c.status === 'active') : []);
      setFilteredCostTypes(costTypesRes.status === 'fulfilled' ? costTypesRes.value.data.filter(c => c.status === 'active') : []);

      // Fetch project settings and project cost codes if we have a project
      if (projectVuid) {
        try {
          const [projectSettingsRes, projectCostCodesRes] = await Promise.all([
            axios.get(`${baseURL}/api/projects/${projectVuid}/settings`),
            axios.get(`${baseURL}/api/projects/${projectVuid}/cost-codes`)
          ]);
          setProjectSettings(projectSettingsRes.data);
          setProjectCostCodes(projectCostCodesRes.data);
          
          // Auto-enable project-specific cost codes if project allows it
          if (projectSettingsRes.data?.allow_project_cost_codes) {
            setUseProjectCostCodes(true);
          }
        } catch (err) {
          console.warn('Could not fetch project settings or cost codes:', err.message);
        }
      }
      
      // Budget lines are already included in the budget response as budget_lines
      // No need to fetch separately - just extract them if needed for compatibility
      const budgetsData = budgetsRes.status === 'fulfilled' ? budgetsRes.value.data : [];
      if (budgetsData.length > 0) {
        const allBudgetLines = [];
        budgetsData.forEach(budget => {
          if (budget.budget_lines && budget.budget_lines.length > 0) {
            allBudgetLines.push(...budget.budget_lines.map(line => ({
              ...line,
              budget_vuid: budget.vuid
            })));
          }
        });
        setBudgetLines(allBudgetLines);
        console.log('Budget lines extracted from API response:', allBudgetLines.length);
        console.log('Budget amounts:', budgetsData.map(b => ({
          vuid: b.vuid,
          description: b.description,
          budget_amount: b.budget_amount,
          lines_count: b.budget_lines?.length || 0
        })));
      }
    } catch (err) {
      setError('Error fetching data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to get the appropriate cost codes based on toggle state
  const getCostCodesForDisplay = () => {
    if (useProjectCostCodes && projectSettings?.allow_project_cost_codes) {
      // Return only project-specific cost codes
      return projectCostCodes.filter(cc => cc.is_project_specific);
    } else {
      // Return global cost codes
      return costCodes.filter(c => c.status === 'active');
    }
  };

  // Function to handle cost code input for project-specific mode
  const handleCostCodeInput = (value, index, isNewLine = false) => {
    if (isNewLine) {
      setNewLineData(prev => ({ ...prev, cost_code_vuid: value }));
    } else {
      const newLines = [...budgetLines];
      newLines[index] = { ...newLines[index], cost_code_vuid: value };
      setBudgetLines(newLines);
    }
  };

  // Function to create project-specific cost code if it doesn't exist
  const createProjectCostCodeIfNeeded = async (costCodeValue) => {
    if (!useProjectCostCodes || !projectSettings?.allow_project_cost_codes) {
      return costCodeValue; // Return as-is for global mode
    }

    // Validate that costCodeValue is not a VUID
    if (costCodeValue && costCodeValue.length === 36 && costCodeValue.includes('-')) {
      console.error('ERROR: Attempted to create project cost code with VUID as code:', costCodeValue);
      throw new Error('Invalid cost code: Cannot use a VUID as a cost code. Please enter a valid cost code (e.g., "01-001", "02-220")');
    }

    // Check if this cost code already exists as a project cost code
    const existingProjectCostCode = projectCostCodes.find(
      cc => cc.is_project_specific && cc.code === costCodeValue
    );

    if (existingProjectCostCode) {
      return existingProjectCostCode.vuid; // Return existing VUID
    }

    // Create new project cost code
    try {
      const baseURL = 'http://localhost:5001';
      const searchParams = new URLSearchParams(location.search);
      const projectVuid = searchParams.get('project');
      
      const response = await axios.post(`${baseURL}/api/project-cost-codes`, {
        project_vuid: projectVuid,
        code: costCodeValue,
        description: `Project-specific cost code: ${costCodeValue}`,
        status: 'active'
      });

      // Add to local state
      setProjectCostCodes(prev => [...prev, response.data]);
      
      return response.data.vuid; // Return new VUID
    } catch (error) {
      console.error('Error creating project cost code:', error);
      throw new Error(`Failed to create project cost code: ${costCodeValue}`);
    }
  };

  // Filter and paginate budgets
  useEffect(() => {
    filterAndPaginateBudgets();
  }, [budgets, searchTerm, projectFilter, typeFilter]);

  // Update form when accounting periods are loaded and form is open
  useEffect(() => {
    if (showCreateForm && !editingBudget && !selectedBudget && accountingPeriods.length > 0) {
      // Only update if accounting_period_vuid is not already set
      if (!formData.accounting_period_vuid) {
        const openPeriods = accountingPeriods.filter(p => p.status === 'open');
        if (openPeriods.length === 1) {
          setFormData(prev => ({
            ...prev,
            accounting_period_vuid: openPeriods[0].vuid
          }));
        }
      }
    }
  }, [accountingPeriods, showCreateForm, editingBudget, selectedBudget]);

  // Check if selected project still exists and reset form if not
  useEffect(() => {
    if (formData.project_vuid && projects.length > 0) {
      const projectExists = projects.find(p => p.vuid === formData.project_vuid);
      if (!projectExists) {
        // Check if this project is from URL parameters - if so, don't clear it
        const searchParams = new URLSearchParams(location.search);
        const urlProjectVuid = searchParams.get('project');
        
        if (formData.project_vuid === urlProjectVuid) {
          console.log('ProjectBudgets: Project from URL not found in loaded projects, but preserving URL context');
          return; // Don't clear the project if it's from URL parameters
        }
        
        // Reset the form if the selected project no longer exists
        setFormData(prev => ({
          ...prev,
          project_vuid: ''
        }));
        setError('The previously selected project no longer exists. Please select a valid project.');
      }
    }
    
    // Also check if the project filter is still valid
    if (projectFilter !== 'all' && projects.length > 0) {
      const filterProjectExists = projects.find(p => p.vuid === projectFilter);
      if (!filterProjectExists) {
        setProjectFilter('all');
        setError('The previously filtered project no longer exists. Showing all projects.');
      }
    }
  }, [projects, formData.project_vuid, projectFilter]);

  // Fetch budget lines when selectedBudget changes - simplified
  useEffect(() => {
    if (selectedBudget && !showCreateForm && !showLinesForm) {
      if (selectedBudget.budget_type === 'revised') {
        fetchIcoLines(selectedBudget.vuid);
      } else {
        // Budget lines are already in selectedBudget object
        if (selectedBudget.budget_lines && selectedBudget.budget_lines.length > 0) {
          const linesWithBudgetVuid = selectedBudget.budget_lines.map(line => ({
            ...line,
            budget_vuid: selectedBudget.vuid
          }));
          setBudgetLines(linesWithBudgetVuid);
        } else {
          fetchBudgetLines(selectedBudget.vuid);
        }
      }
    }
  }, [selectedBudget?.vuid, showCreateForm, showLinesForm]);

  const filterAndPaginateBudgets = () => {
    let filtered = budgets.filter(budget => {
      const matchesSearch = 
        (budget.description && budget.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesProject = projectFilter === 'all' || budget.project_vuid === projectFilter;
      const matchesType = typeFilter === 'all' || budget.budget_type === typeFilter;
      
      return matchesSearch && matchesProject && matchesType;
    });
    
    // Business rule: Only show one original budget per project
    // If there are multiple original budgets for the same project, show only the first one
    const projectOriginalBudgets = new Map();
    const deduplicated = filtered.filter(budget => {
      if (budget.budget_type === 'original') {
        if (projectOriginalBudgets.has(budget.project_vuid)) {
          // Already have an original budget for this project, skip this one
          return false;
        } else {
          // First original budget for this project, keep it
          projectOriginalBudgets.set(budget.project_vuid, budget);
          return true;
        }
      }
      // Not an original budget, keep it
      return true;
    });
    
    setFilteredBudgets(deduplicated);
    setCurrentPage(1);
  };

  // Get current budgets for pagination
  const getCurrentBudgets = () => {
    const indexOfLastBudget = currentPage * budgetsPerPage;
    const indexOfFirstBudget = indexOfLastBudget - budgetsPerPage;
    return filteredBudgets.slice(indexOfFirstBudget, indexOfLastBudget);
  };

  // Helper function to get the change amount for a revised budget
  const getChangeAmountForBudget = (budgetVuid) => {
    if (!internalChangeOrders || internalChangeOrders.length === 0) return + 0;
    
    const ico = internalChangeOrders.find(ico => ico.revised_budget_vuid === budgetVuid);
    if (ico) {
      return parseFloat(ico.total_change_amount) || 0;
    }
    return + 0;
  };

  // Helper function to get the original budget amount (sum of budget lines) - simplified
  const getOriginalBudgetAmount = (budgetVuid) => {
    // First try to get the budget from the budgets array
    const budget = budgets.find(b => b.vuid === budgetVuid);
    if (budget) {
      // Use budget_amount from API if available
      if (budget.budget_amount && parseFloat(budget.budget_amount) > 0) {
        return parseFloat(budget.budget_amount);
      }
      // Otherwise sum up budget_lines from API response
      if (budget.budget_lines && budget.budget_lines.length > 0) {
        const total = budget.budget_lines.reduce((sum, line) => sum + (parseFloat(line.budget_amount) || 0), 0);
        return total;
      }
    }
    // Fallback to budgetLines array (if fetched separately)
    const total = budgetLines.reduce((sum, line) => sum + (parseFloat(line.budget_amount) || 0), 0);
    console.log(`getOriginalBudgetAmount + for budget ${budgetVuid}:`, {
      budget_amount: budget?.budget_amount,
      budget_lines_count: budget?.budget_lines?.length || 0,
      budgetLines_count: budgetLines.length,
      total: total
    });
    return total;
  };

  // Helper function to get the current budget amount from WIP data (includes external change order changes)
  const getCurrentBudgetAmount = (budget) => {
    if (!budget || !budget.project_vuid) return + 0;
    
    // Find the WIP entry for this project
    const wipEntry = wipData.find(entry => entry.project_vuid === budget.project_vuid);
    if (wipEntry && wipEntry.current_budget_amount > 0) {
      return wipEntry.current_budget_amount;
    }
    
    // Fallback: Use budget_amount from API response
    if (budget.budget_amount && parseFloat(budget.budget_amount) > 0) {
      return parseFloat(budget.budget_amount);
    }
    
    // Fallback: Sum budget_lines from API response
    if (budget.budget_lines && budget.budget_lines.length > 0) {
      return budget.budget_lines.reduce((sum, line) => sum + (parseFloat(line.budget_amount) || 0), 0);
    }
    
    // Final fallback: Get budget amount from the budgetLines array (if fetched separately)
    const budgetLinesForThisBudget = budgetLines.filter(line => line.budget_vuid === budget.vuid);
    if (budgetLinesForThisBudget.length > 0) {
      return budgetLinesForThisBudget.reduce((sum, line) => sum + (parseFloat(line.budget_amount) || 0), 0);
    }
    
    // Final fallback: return + 0 if no data available
    return + 0;
  };

  // Fetch external change order lines for a project
  const fetchExternalChangeOrderLines = async (projectVuid) => {
    try {
      const baseURL = 'http://localhost:5001';
      console.log('Fetching external change order lines for project:', projectVuid);
      
      // Get all external change orders for this project
      const ecosResponse = await axios.get(`${baseURL}/api/external-change-orders?project_vuid=${projectVuid}`);
      console.log('All external change orders for project:', ecosResponse.data);
      const ecos = ecosResponse.data.filter(eco => eco.status === 'approved');
      console.log('Approved external change orders:', ecos);
      
      // Get lines for all approved external change orders
      const allEcoLines = [];
      for (const eco of ecos) {
        console.log('Fetching lines for ECO:', eco.vuid);
        const linesResponse = await axios.get(`${baseURL}/api/external-change-orders/${eco.vuid}/lines`);
        console.log('ECO lines response:', linesResponse.data);
        const lines = linesResponse.data.filter(line => line.status === 'active');
        console.log('Active ECO lines:', lines);
        allEcoLines.push(...lines);
      }
      
      setExternalChangeOrderLines(allEcoLines);
      console.log('Final external change order lines set:', allEcoLines);
    } catch (error) {
      console.error('Error fetching external change order lines:', error);
    }
  };

  // Get change order impacts for a specific budget line
  const getChangeOrderImpacts = (budgetLine) => {
    if (!budgetLine || !budgetLine.cost_code_vuid || !budgetLine.cost_type_vuid) {
      console.log('Budget line missing cost code or type:', budgetLine);
      return { external: 0, internal: 0, total: 0 };
    }

    console.log('Checking impacts for budget line:', {
      cost_code_vuid: budgetLine.cost_code_vuid,
      cost_type_vuid: budgetLine.cost_type_vuid,
      availableEcoLines: externalChangeOrderLines.length,
      ecoLines: externalChangeOrderLines
    });

    // Find external change order lines that match this budget line
    const externalImpacts = externalChangeOrderLines.filter(ecoLine => 
      ecoLine.cost_code_vuid === budgetLine.cost_code_vuid && 
      ecoLine.cost_type_vuid === budgetLine.cost_type_vuid
    );

    console.log('External impacts found:', externalImpacts);

    // Find internal change order lines that match this budget line
    const internalImpacts = icoLines.filter(icoLine => 
      icoLine.cost_code_vuid === budgetLine.cost_code_vuid && 
      icoLine.cost_type_vuid === budgetLine.cost_type_vuid
    );

    const externalTotal = externalImpacts.reduce((sum, line) => sum + (parseFloat(line.budget_amount_change) || 0), 0);
    const internalTotal = internalImpacts.reduce((sum, line) => sum + (parseFloat(line.change_amount) || 0), 0);

    console.log('Impact totals:', { external: externalTotal, internal: internalTotal, total: externalTotal + internalTotal });

    return {
      external: externalTotal,
      internal: internalTotal,
      total: externalTotal + internalTotal
    };
  };

  // Helper function to get the revised budget amount (original change orders)
  const getRevisedBudgetAmount = (budgetVuid) => {
    const originalAmount = getOriginalBudgetAmount(budgetVuid);
    
    // Find all ICOs that affect this budget
    const icos = internalChangeOrders.filter(ico => ico.original_budget_vuid === budgetVuid);
    const totalChangeAmount = icos.reduce((sum, ico) => sum + (parseFloat(ico.total_change_amount) || 0), 0);
    
    return originalAmount + totalChangeAmount;
  };

  // Handle form submission for budgets
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent updating finalized budgets
    if (editingBudget && isBudgetFinalized(editingBudget)) {
      setError('This budget is finalized and cannot be modified.');
      return;
    }
    
    // Prevent updating locked budgets (accounting period is closed)
    if (editingBudget && isBudgetLocked(editingBudget)) {
      setError('This budget cannot be modified because its accounting period is closed. Create a change order to modify values.');
      return;
    }
    
    // Validate required fields
    if (!formData.project_vuid || !formData.accounting_period_vuid || !formData.description || !formData.budget_type || !formData.budget_date) {
      setError('Please fill in all required fields (Project, Accounting Period, Description, Budget Type, and Budget Date)');
      return;
    }
    
    // Validate that the selected project exists
    const selectedProject = projects.find(p => p.vuid === formData.project_vuid);
    if (!selectedProject) {
      setError('Selected project no longer exists. Please refresh the page and select a valid project.');
      return;
    }
    
    // Validate that the selected accounting period exists
    const selectedPeriod = accountingPeriods.find(p => p.vuid === formData.accounting_period_vuid);
    if (!selectedPeriod) {
      setError('Selected accounting period no longer exists. Please refresh the page and select a valid accounting period.');
      return;
    }
    
    // Business rule: Only one original budget per project
    if (!editingBudget && formData.budget_type === 'original') {
      const existingOriginalBudget = budgets.find(b => 
        b.project_vuid === formData.project_vuid && 
        b.budget_type === 'original'
      );
      
      if (existingOriginalBudget) {
        setError('This project already has an original budget. Only one original budget is allowed per project. If you need to make changes, create an Internal Change Order instead.');
        return;
      }
    }
    
    // Business rule: Cannot create new budgets if original budget is finalized
    if (!editingBudget) {
      const originalBudget = budgets.find(b => 
        b.project_vuid === formData.project_vuid && 
        b.budget_type === 'original'
      );
      
      if (originalBudget && isBudgetFinalized(originalBudget)) {
        setError('The original budget for this project is finalized. You cannot create new budgets. Create an Internal Change Order to make adjustments.');
        return;
      }
    }
    
    try {
      const baseURL = 'http://localhost:5001';
      
      // Debug logging
      console.log('Creating budget with data:', formData);
      console.log('Available projects:', projects.map(p => ({ vuid: p.vuid, name: p.project_name })));
      console.log('Available accounting periods:', accountingPeriods.map(p => ({ vuid: p.vuid, month: p.month, year: p.year, status: p.status })));
      console.log('Selected accounting period vuid:', formData.accounting_period_vuid);
      
      let response;
      if (editingBudget) {
        response = await axios.put(`${baseURL}/api/project-budgets/${editingBudget.vuid}`, formData);
        await fetchData();
        setSuccessMessage(''); // Clear success message when editing
        resetForm();
      } else {
        // Creating new budget - keep form visible and show lines grid
        response = await axios.post(`${baseURL}/api/project-budgets`, formData);
        await fetchData();
        
        // Get the newly created budget
        const newBudget = response.data;
        if (newBudget && newBudget.vuid) {
          console.log('Budget created successfully:', newBudget);
          
          // Set the newly created budget as selected
          setSelectedBudget(newBudget);
          // Keep showCreateForm true so header stays visible, but also allow lines to be shown
          // Don't hide the form - user should see header and lines together
          
          // Pre-populate the line form with the new budget's VUID
          setLineFormData(prev => ({
            ...prev,
            budget_vuid: newBudget.vuid
          }));
          
          // Initialize budget lines as empty array
          setBudgetLines([]);
          
          // Ensure the budget object has all necessary fields for the grid
          const budgetWithLines = {
            ...newBudget,
            budget_lines: [] // Initialize empty lines array
          };
          setSelectedBudget(budgetWithLines);
          
          // Show success message
          setSuccessMessage(`Budget "${newBudget.description}" created successfully! Add your budget lines below.`);
          setError(''); // Clear any previous errors
          
          // Don't reset form - keep it visible with the entered data
          setEditingBudget(null);
          return; // Don't call resetForm again below
        }
      }
      
      setError(''); // Clear any previous errors
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        const errorMsg = err.response.data.error;
        if (errorMsg.includes('ForeignKeyViolation') && errorMsg.includes('project_vuid')) {
          setError('The selected project no longer exists. Please refresh the page and select a valid project from the dropdown.');
          // Refresh projects list to ensure we have current data
          fetchData();
        } else {
          setError('Error saving budget: ' + errorMsg);
        }
      } else {
        setError('Error saving budget: ' + err.message);
      }
      setSuccessMessage(''); // Clear success message on error
    }
  };



  // Fetch budget lines for a specific budget - simplified
  const fetchBudgetLines = async (budgetVuid) => {
    const baseURL = 'http://localhost:5001';
    try {
      // Budget lines are already included in the budget object from the API
      // Find the budget in the budgets array and extract its lines
      const budget = budgets.find(b => b.vuid === budgetVuid);
      if (budget && budget.budget_lines) {
        const linesWithBudgetVuid = budget.budget_lines.map(line => {
          // Validate and clear invalid cost_code_vuid
          const isInvalid = line.cost_code_vuid === line.vuid || 
                           (line.cost_code_vuid && line.cost_code_vuid.length === 36 && 
                            line.cost_code_vuid.includes('-') &&
                            (!line.cost_code || (line.cost_code.code && line.cost_code.code.length === 36)));
          if (isInvalid) {
            console.warn('Clearing invalid cost_code_vuid for line:', line.vuid);
            return {
              ...line,
              budget_vuid: budgetVuid,
              cost_code_vuid: '',
              cost_code: null
            };
          }
          return {
            ...line,
            budget_vuid: budgetVuid
          };
        });
        setBudgetLines(linesWithBudgetVuid);
        console.log(`Budget lines loaded from budgets array for ${budgetVuid}:`, linesWithBudgetVuid.length);
        setError(''); // Clear any previous errors
      } else {
        // If budget not found in state, fetch the budget directly (which includes budget_lines)
        const budgetResponse = await axios.get(`${baseURL}/api/project-budgets/${budgetVuid}`);
        if (budgetResponse.data && budgetResponse.data.budget_lines) {
          const linesWithBudgetVuid = budgetResponse.data.budget_lines.map(line => {
            // Validate and clear invalid cost_code_vuid
            const isInvalid = line.cost_code_vuid === line.vuid || 
                             (line.cost_code_vuid && line.cost_code_vuid.length === 36 && 
                              line.cost_code_vuid.includes('-') &&
                              (!line.cost_code || (line.cost_code.code && line.cost_code.code.length === 36)));
            if (isInvalid) {
              console.warn('Clearing invalid cost_code_vuid for line:', line.vuid);
              return {
                ...line,
                budget_vuid: budgetVuid,
                cost_code_vuid: '',
                cost_code: null
              };
            }
            return {
              ...line,
              budget_vuid: budgetVuid
            };
          });
          setBudgetLines(linesWithBudgetVuid);
          console.log(`Budget lines loaded from API for ${budgetVuid}:`, linesWithBudgetVuid.length);
          setError(''); // Clear any previous errors
        } else {
          setBudgetLines([]);
          console.warn(`No budget lines found for budget ${budgetVuid}`);
        }
      }
      
      // Also fetch project cost codes for this budget's project if we don't have them yet
      const budgetForCostCodes = budget || budgets.find(b => b.vuid === budgetVuid);
      if (budgetForCostCodes && budgetForCostCodes.project_vuid) {
        // Check if we already have project cost codes for this project
        const existingProjectCostCodes = projectCostCodes.filter(cc => cc.project_vuid === budgetForCostCodes.project_vuid);
        if (existingProjectCostCodes.length === 0) {
          try {
            const projectCostCodesRes = await axios.get(`${baseURL}/api/projects/${budgetForCostCodes.project_vuid}/cost-codes`);
            setProjectCostCodes(prev => {
              // Add new project cost codes, avoiding duplicates
              const newCodes = projectCostCodesRes.data.filter(newCode => 
                !prev.some(existingCode => existingCode.vuid === newCode.vuid)
              );
              return [...prev, ...newCodes];
            });
          } catch (err) {
            console.warn('Could not fetch project cost codes:', err.message);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching budget lines:', err);
      // Don't set error for missing lines endpoint - it's expected (404)
      if (err.response?.status !== 404 && !err.message.includes('404')) {
        setError('Error fetching budget lines: ' + err.message);
      }
    }
  };

  // Fetch Internal Change Order lines for a specific revised budget
  const fetchIcoLines = async (budgetVuid) => {
    try {
      const baseURL = 'http://localhost:5001';
      
      // Find the ICO that created this revised budget
      const ico = internalChangeOrders.find(ico => ico.revised_budget_vuid === budgetVuid);
      if (!ico) {
        setError('No Internal Change Order found for this revised budget');
        return;
      }
      
      // Fetch the ICO lines
      const response = await axios.get(`${baseURL}/api/internal-change-orders/${ico.vuid}/lines`);
      setIcoLines(response.data);
      
      // Clear budget lines for this view
      setBudgetLines([]);
    } catch (err) {
      setError('Error fetching Internal Change Order lines: ' + err.message);
    }
  };

  // Initialize form with defaults (including accounting period)
  const initializeForm = () => {
    // Preserve project context from URL when initializing
    const searchParams = new URLSearchParams(location.search);
    const urlProjectVuid = searchParams.get('project');
    
    // Get open accounting periods
    const openPeriods = accountingPeriods.filter(p => p.status === 'open');
    let defaultPeriodVuid = '';
    if (openPeriods.length === 1) {
      // Prefill if only one open period
      defaultPeriodVuid = openPeriods[0].vuid;
    } else if (openPeriods.length > 1) {
      // If multiple, don't prefill - user must select
      defaultPeriodVuid = '';
    }
    
    setFormData({
      project_vuid: urlProjectVuid || '',
      accounting_period_vuid: defaultPeriodVuid,
      description: '',
      budget_type: 'original',
      budget_date: new Date().toISOString().split('T')[0], // Set today's date as default
      finalized: false
    });
  };

  // Reset forms
  const resetForm = () => {
    initializeForm();
    setEditingBudget(null);
    setShowCreateForm(false);
    setShowLinesForm(false);
    setSuccessMessage(''); // Clear success message
  };

  // Handle opening create form
  const handleCreateBudget = () => {
    // Check if there's a project filter from URL
    const searchParams = new URLSearchParams(location.search);
    const urlProjectVuid = searchParams.get('project');
    
    // If there's a project filter, check business rules
    if (urlProjectVuid) {
      const existingOriginalBudget = budgets.find(b => 
        b.project_vuid === urlProjectVuid && 
        b.budget_type === 'original'
      );
      
      if (existingOriginalBudget) {
        if (isBudgetFinalized(existingOriginalBudget)) {
          setError('The original budget for this project is finalized. You cannot create new budgets. Create an Internal Change Order to make adjustments.');
          return;
        } else {
          setError('This project already has an original budget. Only one original budget is allowed per project. If you need to make changes, create an Internal Change Order instead.');
          return;
        }
      }
    }
    
    initializeForm();
    setShowCreateForm(true);
    setEditingBudget(null);
    setSelectedBudget(null);
  };



  // Handle edit budget
  const handleEditBudget = (budget) => {
    // Prevent editing finalized budgets
    if (isBudgetFinalized(budget)) {
      alert('This budget is finalized and cannot be edited.');
      return;
    }
    
    setEditingBudget(budget);
    setFormData({
      project_vuid: budget.project_vuid,
      accounting_period_vuid: budget.accounting_period_vuid || '',
      description: budget.description,
      budget_type: budget.budget_type,
      budget_date: budget.budget_date || new Date().toISOString().split('T')[0],
      finalized: budget.finalized || false
    });
    setShowCreateForm(true);
  };



  // Handle delete budget
  const handleDeleteBudget = async (vuid) => {
    if (window.confirm('Are you sure you want to delete this budget? This will also delete all associated budget lines.')) {
      try {
        const baseURL = 'http://localhost:5001';
        await axios.delete(`${baseURL}/api/project-budgets/${vuid}`);
        
        // If we were viewing lines for this budget, clear the view
        if (selectedBudget && selectedBudget.vuid === vuid) {
          setSelectedBudget(null);
          setBudgetLines([]);
          setShowLinesForm(false);
        }
        
        fetchData();
        setError(''); // Clear any previous errors
      } catch (err) {
        console.error('Error deleting budget:', err);
        if (err.response && err.response.data && err.response.data.error) {
          setError('Error deleting budget: ' + err.response.data.error);
        } else {
          setError('Error deleting budget: ' + err.message);
        }
      }
    }
  };

  // Handle delete line
  const handleDeleteLine = async (vuid) => {
    if (window.confirm('Are you sure you want to delete this budget line?')) {
      try {
        const baseURL = 'http://localhost:5001';
        await axios.delete(`${baseURL}/api/project-budgets/lines/${vuid}`);
        // Refresh budget data to get updated budget_lines
        await fetchData();
        // Then update budget lines from the refreshed budget
        const refreshedBudget = budgets.find(b => b.vuid === selectedBudget.vuid);
        if (refreshedBudget && refreshedBudget.budget_lines) {
          const linesWithBudgetVuid = refreshedBudget.budget_lines.map(line => ({
            ...line,
            budget_vuid: selectedBudget.vuid
          }));
          setBudgetLines(linesWithBudgetVuid);
        }
        setError(''); // Clear any previous errors
      } catch (err) {
        console.error('Error deleting budget line:', err);
        if (err.response && err.response.data && err.response.data.error) {
          setError('Error deleting budget line: ' + err.response.data.error);
        } else {
          setError('Error deleting budget line: ' + err.message);
        }
      }
    }
  };

  // Handle download template
  const handleDownloadTemplate = async () => {
    if (!selectedBudget || !selectedBudget.vuid) {
      setError('Please select a budget first');
      return;
    }
    
    try {
      setError('');
      setSuccessMessage('');
      
      const baseURL = 'http://localhost:5001';
      const response = await axios.get(
        `${baseURL}/api/project-budgets/${selectedBudget.vuid}/download-template`,
        {
          responseType: 'blob' // Important for file downloads
        }
      );
      
      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'budget_template.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      if (error.response && error.response.data) {
        // Try to parse error message from blob if it's an error response
        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            const errorJson = JSON.parse(text);
            setError('Failed to download template: ' + (errorJson.error || 'Unknown error'));
          } catch {
            setError('Failed to download template: ' + error.response.statusText);
          }
        } else {
          setError('Failed to download template: ' + (error.response.data.error || error.response.statusText));
        }
      } else {
        setError('Failed to download template: ' + error.message);
      }
    }
  };

  // Handle CSV upload
  const handleCsvUpload = async (event) => {
    console.log('CSV upload triggered', event.target.files);
    
    // Check if a budget is selected
    if (!selectedBudget || !selectedBudget.vuid) {
      setError('Please select a budget first');
      return;
    }
    
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    // Validate file type
    const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.xlsx')) {
      setError('Please select a CSV or Excel file (.csv or .xlsx)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setError('');
      setSuccessMessage('');

      const baseURL = 'http://localhost:5001';
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${baseURL}/api/project-budgets/${selectedBudget.vuid}/upload-csv`,
        formData
      );

      if (response.data.errors && response.data.errors.length > 0) {
        // Show validation errors
        setError(`Validation errors found:\n${response.data.errors.join('\n')}`);
      } else {
        // Success
        setSuccessMessage(response.data.message || `Successfully created ${response.data.created_lines?.length || 0} budget lines`);
        // Refresh budget data to get updated budget_lines
        await fetchData();
        // Then update budget lines from the refreshed budget
        const refreshedBudget = budgets.find(b => b.vuid === selectedBudget.vuid);
        if (refreshedBudget && refreshedBudget.budget_lines) {
          const linesWithBudgetVuid = refreshedBudget.budget_lines.map(line => ({
            ...line,
            budget_vuid: selectedBudget.vuid
          }));
          setBudgetLines(linesWithBudgetVuid);
        }
      }

    } catch (err) {
      console.error('Error uploading CSV:', err);
      if (err.response && err.response.data) {
        if (err.response.data.errors && err.response.data.errors.length > 0) {
          setError(`Upload failed with errors:\n${err.response.data.errors.join('\n')}`);
        } else {
          setError('Upload failed: ' + (err.response.data.error || err.response.data.message || 'Unknown error'));
        }
      } else {
        setError('Upload failed: ' + err.message);
      }
    } finally {
      // Clear the file input
      event.target.value = '';
    }
  };

  // Handle view budget lines
  const handleViewLines = async (budget) => {
    // Always fetch fresh budget data from API to ensure we have the correct budget and lines
    const baseURL = 'http://localhost:5001';
    try {
      const budgetResponse = await axios.get(`${baseURL}/api/project-budgets/${budget.vuid}`);
      const freshBudget = budgetResponse.data;
      
      // Set the fresh budget data as selectedBudget
      setSelectedBudget(freshBudget);
      
      if (freshBudget.budget_type === 'revised') {
        // For revised budgets (Internal Change Orders), fetch ICO lines instead
        await fetchIcoLines(freshBudget.vuid);
      } else {
        // For original budgets, use budget lines from the fresh budget object
        if (freshBudget.budget_lines && freshBudget.budget_lines.length > 0) {
          const linesWithBudgetVuid = freshBudget.budget_lines.map(line => ({
            ...line,
            budget_vuid: freshBudget.vuid
          }));
          setBudgetLines(linesWithBudgetVuid);
        } else {
          await fetchBudgetLines(freshBudget.vuid);
        }
      }
      
      // Fetch external change order lines for this project to show impacts
      if (freshBudget.project_vuid) {
        await fetchExternalChangeOrderLines(freshBudget.project_vuid);
      }
    } catch (error) {
      console.error('Error fetching budget data:', error);
      setError('Error loading budget data. Please try again.');
      // Fallback to using the budget object passed in
      setSelectedBudget(budget);
      if (budget.budget_type === 'revised') {
        await fetchIcoLines(budget.vuid);
      } else {
        await fetchBudgetLines(budget.vuid);
      }
    }
    
    setShowLinesForm(false);
    setShowCreateForm(false);
    setEditingBudget(null);
  };

  // Handle hide budget lines
  const handleHideLines = () => {
    setSelectedBudget(null);
    setBudgetLines([]);
    setIcoLines([]);
    setShowLinesForm(false);
    setShowCreateForm(false);
    setEditingBudget(null);
  };

  // Integration functions
  const fetchIntegrations = async () => {
    try {
      const baseURL = 'http://localhost:5001';
      const response = await axios.get(`${baseURL}/api/integrations`);
      
      // Filter integrations to only show those that have 'budgets' enabled
      const filteredIntegrations = response.data.filter(integration => 
        integration.enabled_objects && integration.enabled_objects.budgets === true
      );
      
      setIntegrations(filteredIntegrations);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const handleIntegrationModal = (budget) => {
    setBudgetForIntegration(budget);
    setSelectedIntegration(null);
    setShowIntegrationModal(true);
    fetchIntegrations();
  };

  const handleSendToIntegration = async () => {
    if (!selectedIntegration) {
      alert('Please select an integration');
      return;
    }

    if (selectedIntegration.integration_type !== 'procore') {
      alert('Budget sending is currently only supported for Procore integrations');
      return;
    }

    // Get project VUID from the budget
    const projectVuid = budgetForIntegration?.project_vuid;
    if (!projectVuid) {
      alert('Budget does not have an associated project');
      return;
    }

    // Initialize progress
    setProgressSteps([
      { id: 'step1', name: 'Fetching Segment IDs', status: 'pending', details: '' },
      { id: 'step2', name: 'Creating Cost Code Segment Items', status: 'pending', details: '', count: 0, total: 0 },
      { id: 'step3', name: 'Creating Cost Type Segment Items', status: 'pending', details: '', count: 0, total: 0 },
      { id: 'step4', name: 'Creating WBS Codes', status: 'pending', details: '', count: 0, total: 0 },
      { id: 'step5', name: 'Creating Budget Lines', status: 'pending', details: '', count: 0, total: 0 }
    ]);
    setProgressErrors([]);
    setProgressComplete(false);
    setShowProgressModal(true);

    try {
      // Update step 1 to in_progress
      setProgressSteps(prev => prev.map(step => 
        step.id === 'step1' ? { ...step, status: 'in_progress', details: 'Fetching WBS segments from Procore...' } : step
      ));

      console.log('Sending budget to Procore:', {
        integration_vuid: selectedIntegration.vuid,
        project_vuid: projectVuid,
        budget_vuid: budgetForIntegration?.vuid,
        url: `${API_BASE_URL}/api/integrations/${selectedIntegration.vuid}/send-budget`
      });

      // Call the send-budget endpoint
      const response = await axios.post(
        `${API_BASE_URL}/api/integrations/${selectedIntegration.vuid}/send-budget`,
        {
          project_vuid: projectVuid,
          budget_vuid: budgetForIntegration?.vuid
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Budget send response:', response.data);

      // Update progress based on response
      if (response.data.success) {
        const wbsSummary = response.data.wbs_codes || {};
        const budgetLinesSummary = response.data.budget_lines || {};

        // Update step 1 (Segment IDs)
        setProgressSteps(prev => prev.map(step => 
          step.id === 'step1' 
            ? { ...step, status: 'completed', details: 'Segment IDs retrieved and stored' }
            : step
        ));

        // Update step 2 (Cost Code Segment Items)
        setProgressSteps(prev => prev.map(step => 
          step.id === 'step2' 
            ? { 
                ...step, 
                status: 'completed', 
                count: wbsSummary.cost_code_segment_items?.created || 0,
                total: wbsSummary.cost_code_segment_items?.total || 0,
                details: `Created ${wbsSummary.cost_code_segment_items?.created || 0} of ${wbsSummary.cost_code_segment_items?.total || 0} cost code segment items`
              }
            : step
        ));

        // Update step 3 (Cost Type Segment Items)
        setProgressSteps(prev => prev.map(step => 
          step.id === 'step3' 
            ? { 
                ...step, 
                status: 'completed', 
                count: wbsSummary.cost_type_segment_items?.created || 0,
                total: wbsSummary.cost_type_segment_items?.total || 0,
                details: `Created ${wbsSummary.cost_type_segment_items?.created || 0} of ${wbsSummary.cost_type_segment_items?.total || 0} cost type segment items`
              }
            : step
        ));

        // Update step 4 (WBS Codes)
        setProgressSteps(prev => prev.map(step => 
          step.id === 'step4' 
            ? { 
                ...step, 
                status: 'completed', 
                count: wbsSummary.wbs_codes?.created || 0,
                total: wbsSummary.wbs_codes?.total || 0,
                details: `Created ${wbsSummary.wbs_codes?.created || 0} of ${wbsSummary.wbs_codes?.total || 0} WBS codes`
              }
            : step
        ));

        // Update step 5 (Budget Lines)
        setProgressSteps(prev => prev.map(step => 
          step.id === 'step5' 
            ? { 
                ...step, 
                status: 'completed', 
                count: budgetLinesSummary.created || 0,
                total: budgetLinesSummary.total || 0,
                details: `Created ${budgetLinesSummary.created || 0} of ${budgetLinesSummary.total || 0} budget lines`
              }
            : step
        ));

        // Show errors if any
        if (response.data.budget_lines?.errors && response.data.budget_lines.errors.length > 0) {
          setProgressErrors(response.data.budget_lines.errors);
        } else if (budgetLinesSummary.errors && budgetLinesSummary.errors.length > 0) {
          setProgressErrors(budgetLinesSummary.errors);
        }

        setProgressComplete(true);
      } else {
        // Handle errors - check if backend returned step-by-step status
        const errorMsg = response.data.error || 'Unknown error occurred';
        setProgressErrors(response.data.errors || [errorMsg]);
        
        if (response.data.steps) {
          // Backend provided step-by-step status - use it
          setProgressSteps(prev => prev.map(step => {
            const stepId = step.id;
            const backendStep = response.data.steps[stepId];
            if (backendStep) {
              return {
                ...step,
                status: backendStep.status === 'completed' ? 'completed' : 
                        backendStep.status === 'failed' ? 'error' : 
                        backendStep.status === 'not_started' ? 'pending' : step.status,
                details: backendStep.message || step.details
              };
            }
            return step;
          }));
        } else {
          // Fallback: mark all pending/in_progress steps as error
          setProgressSteps(prev => prev.map(step => 
            step.status === 'pending' || step.status === 'in_progress'
              ? { ...step, status: 'error', details: errorMsg }
              : step
          ));
        }
      }
    } catch (error) {
      console.error('Error sending budget to integration:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        statusText: error.response?.statusText
      });
      
      // Log the full response if available
      if (error.response?.data) {
        console.error('Full error response data:', JSON.stringify(error.response.data, null, 2));
      }
      
      // Determine the error message
      let errorMsg = 'Failed to send budget to integration';
      
      // Check if it's a real network error (no response at all)
      // Note: CORS errors and network failures don't have error.response
      if (!error.response) {
        if (error.code === 'ECONNREFUSED') {
          errorMsg = `Cannot connect to server at ${API_BASE_URL}. Please ensure Flask is running.`;
        } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.message.includes('Network')) {
          errorMsg = `Network error: ${error.message}. This might be a CORS issue or the server is not responding. Check browser console for details.`;
        } else {
          errorMsg = `Request failed: ${error.message || error.code || 'Unknown error'}. Check browser console (F12) for details.`;
        }
      } else if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMsg = 'Authentication failed. Please log in again.';
        } else if (error.response.status === 404) {
          errorMsg = `Endpoint not found: ${error.response.config?.url || 'Unknown URL'}`;
        } else if (error.response.status === 500) {
          // Try to extract error message from response
          const errorData = error.response.data;
          if (errorData) {
            errorMsg = errorData.error || errorData.message || JSON.stringify(errorData);
          } else {
            errorMsg = 'Server error occurred. Check Flask logs for details.';
          }
        } else {
          errorMsg = error.response.data?.error || `Server error: ${error.response.status} - ${error.response.statusText}`;
        }
      } else {
        errorMsg = error.message || 'Unknown error occurred';
      }
      
      // Check if backend provided step-by-step status in error response
      const errorData = error.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        setProgressErrors(errorData.errors);
      } else {
        setProgressErrors([errorMsg]);
      }
      
      // Use step-by-step status if provided by backend
      if (errorData?.steps) {
        setProgressSteps(prev => prev.map(step => {
          const stepId = step.id;
          const backendStep = errorData.steps[stepId];
          if (backendStep) {
            return {
              ...step,
              status: backendStep.status === 'completed' ? 'completed' : 
                      backendStep.status === 'failed' ? 'error' : 
                      backendStep.status === 'not_started' ? 'pending' : step.status,
              details: backendStep.message || step.details
            };
          }
          return step;
        }));
      } else {
        // Fallback: Mark all pending steps as error
        setProgressSteps(prev => prev.map(step => 
          step.status === 'pending' || step.status === 'in_progress'
            ? { ...step, status: 'error', details: errorMsg }
            : step
        ));
      }
      setProgressComplete(true); // Mark as complete so user can close the modal
    }
  };

  const handleRetrieveModal = () => {
    setRetrieveIntegration(null);
    setShowRetrieveModal(true);
    fetchIntegrations();
  };

  // Helper functions for budget selection
  const handleSelectAllBudgets = () => {
    // Only select budget lines that haven't been imported yet
    const newBudgets = retrievedBudgetsData.filter(b => !b.already_imported);
    setSelectedBudgetsToImport(newBudgets);
  };

  const handleDeselectAllBudgets = () => {
    setSelectedBudgetsToImport([]);
  };

  const handleImportBudgets = async () => {
    if (selectedBudgetsToImport.length === 0) {
      alert('Please select at least one budget line to import');
      return;
    }

    try {
      // Get the current project from the form or selected project
      const projectVuid = formData.project_vuid || (projectFilter !== 'all' ? projectFilter : null);
      
      if (!projectVuid) {
        alert('Please select a project first before importing budgets');
        return;
      }

      const baseURL = 'http://localhost:5001';
      let importedCount = 0;
      let skippedCount = 0;
      
      // Step 1: Refresh WBS codes (cost codes and cost types) from Procore
      try {
        const refreshResponse = await axios.post(
          `${baseURL}/api/integrations/${retrieveIntegration.vuid}/refresh-wbs-codes`,
          { project_vuid: projectVuid }
        );
        console.log('WBS codes refreshed:', refreshResponse.data.message || 'Success');
        
        // Refresh cost codes and cost types in the UI (including project-specific)
        const [costCodesRes, costTypesRes, projectCostCodesRes] = await Promise.all([
          axios.get(`${baseURL}/api/costcodes`).catch(() => ({ data: [] })),
          axios.get(`${baseURL}/api/costtypes`).catch(() => ({ data: [] })),
          axios.get(`${baseURL}/api/projects/${projectVuid}/cost-codes`).catch(() => ({ data: [] }))
        ]);
        
        // Combine global and project-specific cost codes
        const globalCostCodes = costCodesRes.data || [];
        const projectCostCodesList = (projectCostCodesRes.data || []).filter(c => c.is_project_specific);
        const allCostCodes = [...globalCostCodes, ...projectCostCodesList];
        
        setCostCodes(costCodesRes.data || []); // Keep global separate
        setProjectCostCodes(projectCostCodesList); // Set project-specific separately
        setCostTypes(costTypesRes.data || []);
      } catch (refreshError) {
        console.error('Error refreshing WBS codes:', refreshError);
        // Continue anyway - cost codes/types might already be synced
      }
      
      // Step 2: Check if a budget already exists for this project
      const existingBudgets = budgets.filter(b => b.project_vuid === projectVuid && b.budget_type === 'original');
      let budgetVuid = null;
      let isExistingBudget = false;
      
      if (existingBudgets.length > 0) {
        // Use the first existing original budget
        budgetVuid = existingBudgets[0].vuid;
        isExistingBudget = true;
        console.log(`Using existing budget: ${budgetVuid}`);
      } else {
        // Create a new budget if none exists
      const defaultAccountingPeriod = accountingPeriods.find(p => p.status === 'open');
      if (!defaultAccountingPeriod) {
        alert('No open accounting period found. Please create an open accounting period first.');
        return;
      }

      const budgetResponse = await axios.post(`${baseURL}/api/project-budgets`, {
        project_vuid: projectVuid,
        accounting_period_vuid: defaultAccountingPeriod.vuid,
        description: `Imported Budget from ${retrieveIntegration.integration_name}`,
        budget_type: 'original',
        budget_date: new Date().toISOString().split('T')[0],
        finalized: false
      });

      if (budgetResponse.data && budgetResponse.data.vuid) {
          budgetVuid = budgetResponse.data.vuid;
        } else {
          alert('Failed to create budget');
          return;
        }
      }

      if (budgetVuid) {
        
        // Import selected budget lines
        for (const budgetLineData of selectedBudgetsToImport) {
          try {
            // Find matching cost code and cost type
            // Backend returns cost_code as {code, description} and cost_type as {name, abbreviation}
            const costCodeToMatch = budgetLineData.cost_code_obj?.code || budgetLineData.cost_code?.code || budgetLineData.cost_code;
            const costTypeToMatch = budgetLineData.cost_type_obj?.name || budgetLineData.cost_type?.name || budgetLineData.cost_type;
            
            console.log(`Matching cost code: "${costCodeToMatch}", cost type: "${costTypeToMatch}"`);
            console.log(`Budget line data:`, budgetLineData);
            
            // Search in both global cost codes and project-specific cost codes
            const allCostCodes = [...costCodes, ...projectCostCodes];
            const matchingCostCode = allCostCodes.find(c => 
              c.code === costCodeToMatch && (c.status === 'active' || !c.status)
            );
            
            const matchingCostType = costTypes.find(c => 
              c.cost_type === costTypeToMatch && (c.status === 'active' || !c.status)
            );
            
            if (!matchingCostCode) {
              console.log(`Cost code not found: ${costCodeToMatch}. Available codes:`, allCostCodes.map(c => c.code));
            }
            if (!matchingCostType) {
              console.log(`Cost type not found: ${costTypeToMatch}. Available types:`, costTypes.map(c => c.cost_type));
            }
            
            if (matchingCostCode && matchingCostType) {
              // Create budget line
              const lineResponse = await axios.post(`${baseURL}/api/project-budgets/${budgetVuid}/lines`, {
                budget_vuid: budgetVuid,
                cost_code_vuid: matchingCostCode.vuid,
                cost_type_vuid: matchingCostType.vuid,
                budget_amount: parseFloat(budgetLineData.budget_amount) || 0,
                notes: budgetLineData.notes || '',
                status: 'active'
              });
              
              // Create external system ID mapping for the imported budget line
              if (lineResponse.data && lineResponse.data.vuid && budgetLineData.procore_id) {
                try {
                  await axios.post(`${baseURL}/api/external-system-ids`, {
                    object_vuid: lineResponse.data.vuid,
                    object_type: 'budget_line_item',
                    integration_vuid: retrieveIntegration.vuid,
                    external_id: budgetLineData.procore_id.toString(),
                    external_object_type: 'budget_line_item'
                  });
                } catch (mappingError) {
                  console.error('Error creating external system ID mapping for budget line:', mappingError);
                  // Continue even if mapping fails
                }
              }
              
              importedCount++;
            } else {
              console.log(`Skipping budget line - cost code or type not found: ${budgetLineData.cost_code}/${budgetLineData.cost_type}`);
              skippedCount++;
            }
          } catch (error) {
            console.error(`Error importing budget line:`, error);
            skippedCount++;
          }
        }
        
        // Create external system ID mapping for the budget (only if new budget was created)
        if (!isExistingBudget) {
        try {
          await axios.post(`${baseURL}/api/external-system-ids`, {
              object_vuid: budgetVuid,
            object_type: 'project_budget',
            integration_name: retrieveIntegration.integration_name,
            external_id: `imported_${Date.now()}`,
            metadata: {
              imported_lines: importedCount,
              skipped_lines: skippedCount,
              integration_source: retrieveIntegration.integration_name
            }
          });
            console.log(`External system ID mapping created for budget ${budgetVuid}`);
        } catch (mappingError) {
          console.error('Error creating external system ID mapping:', mappingError);
          }
        }

        // Show success message
        const budgetMessage = isExistingBudget
          ? `Budget lines added to existing budget!\n\nImported: ${importedCount} lines\nSkipped: ${skippedCount} lines`
          : `Budget import completed!\n\nImported: ${importedCount} lines\nSkipped: ${skippedCount} lines\n\nA new budget has been created for the selected project.`;
        alert(budgetMessage);
        
        // Close modals and refresh data
        setShowRetrievedBudgetsModal(false);
        setShowRetrieveModal(false);
        setRetrieveIntegration(null);
        setRetrievedBudgetsData([]);
        setSelectedBudgetsToImport([]);
        
        // Refresh the data
        fetchData();
        
        // Also fetch the budget lines for the budget
        await fetchBudgetLines(budgetVuid);
      }
    } catch (error) {
      console.error('Error importing budgets:', error);
      alert('Error importing budgets: ' (error.response?.data?.error || error.message));
    }
  };

  const handleRetrieveBudgets = async () => {
    if (!retrieveIntegration) {
      alert('Please select an integration');
      return;
    }

    try {
      console.log(`Retrieving budgets from integration ${retrieveIntegration.integration_name}`);
      
      let retrievedBudgets = [];
      
      // Handle different integration types
      if (retrieveIntegration.integration_type === 'procore') {
        // Get project_vuid from URL params
        const searchParams = new URLSearchParams(location.search);
        const projectVuid = searchParams.get('project');
        
        if (!projectVuid) {
          alert('Please navigate to a project page first to retrieve budget lines from Procore.');
          return;
        }
        
        // Call the real Procore budget lines API
        const baseURL = 'http://localhost:5001';
        const response = await axios.get(
          `${baseURL}/api/integrations/${retrieveIntegration.vuid}/retrieve-budget-lines?project_vuid=${projectVuid}`
        );
        
        if (response.data && response.data.budget_lines) {
          // Get all budget lines from Procore
          const budgetLines = response.data.budget_lines;
          
          if (budgetLines.length === 0) {
            alert('No budget lines found in Procore for this project.');
            return;
          }
          
          // Debug: Log first budget line structure to see what we're getting
          if (budgetLines.length > 0) {
            console.log('Sample budget line from backend:', JSON.stringify(budgetLines[0], null, 2));
            console.log('Cost code object:', budgetLines[0].cost_code);
            console.log('Cost type object:', budgetLines[0].cost_type);
          }
          
          retrievedBudgets = budgetLines.map((procoreBudgetLine, idx) => {
            // Backend sends cost_code and cost_type as objects with 'code'/'description' and 'name'/'abbreviation'
            const costCodeObj = procoreBudgetLine.cost_code || {};
            const costCodeCode = (typeof costCodeObj === 'object' && costCodeObj !== null) ? (costCodeObj.code || '') : (typeof costCodeObj === 'string' ? costCodeObj : '');
            const costCodeDesc = (typeof costCodeObj === 'object' && costCodeObj !== null) ? (costCodeObj.description || '') : '';
            
            const costTypeObj = procoreBudgetLine.cost_type || {};
            const costTypeName = (typeof costTypeObj === 'object' && costTypeObj !== null) ? (costTypeObj.name || '') : (typeof costTypeObj === 'string' ? costTypeObj : '');
            const costTypeAbbr = (typeof costTypeObj === 'object' && costTypeObj !== null) ? (costTypeObj.abbreviation || '') : '';
            
            // Safely extract division, category, subcategory
            const divisionObj = procoreBudgetLine.division || {};
            const divisionName = (typeof divisionObj === 'object' && divisionObj !== null) ? (divisionObj.name || '') : (typeof divisionObj === 'string' ? divisionObj : '');
            
            const categoryObj = procoreBudgetLine.category || {};
            const categoryName = (typeof categoryObj === 'object' && categoryObj !== null) ? (categoryObj.name || '') : (typeof categoryObj === 'string' ? categoryObj : '');
            
            const subcategoryObj = procoreBudgetLine.subcategory || {};
            const subcategoryName = (typeof subcategoryObj === 'object' && subcategoryObj !== null) ? (subcategoryObj.name || '') : (typeof subcategoryObj === 'string' ? subcategoryObj : '');
            
            return {
              cost_code: costCodeCode,
              cost_code_description: costCodeDesc,
              cost_code_obj: (typeof costCodeObj === 'object' && costCodeObj !== null) ? costCodeObj : null,
              cost_type: costTypeName,
              cost_type_abbreviation: costTypeAbbr,
              cost_type_obj: (typeof costTypeObj === 'object' && costTypeObj !== null) ? costTypeObj : null,
            budget_amount: procoreBudgetLine.budget_amount,
            notes: procoreBudgetLine.notes || '',
            // Additional fields from Procore
            procore_id: procoreBudgetLine.id,
              already_imported: procoreBudgetLine.already_imported || false,
              division: divisionName,
              category: categoryName,
              subcategory: subcategoryName
            };
          });
        }
      } else {
        // Handle other integration types (placeholder)
        alert(`Integration type ${retrieveIntegration.integration_type} is not yet implemented`);
        return;
      }
      
      if (retrievedBudgets.length > 0) {
        // Show the retrieved budgets in a modal for review
        setRetrievedBudgetsData(retrievedBudgets);
        setSelectedBudgetsToImport([]); // Reset selection when opening modal
        setShowRetrievedBudgetsModal(true);
      } else {
        alert('No budgets found in the integration');
      }
      
      // Don't close the modal yet - let user review the budgets first
    } catch (error) {
      console.error('Error retrieving budgets from integration:', error);
      alert('Error retrieving budgets from integration: ' + (error.response?.data?.error || error.message));
    }
  };

  // Helper functions
  const getProjectName = (projectVuid) => {
    const project = projects.find(p => p.vuid === projectVuid);
    return project ? `${project.project_number} - ${project.project_name}` : 'Unknown Project';
  };

  const getCostCodeName = (costCodeVuid) => {
    // If costCodeVuid is empty or invalid, return empty
    if (!costCodeVuid) {
      return 'Unknown Cost Code';
    }
    
    // If costCodeVuid looks like a VUID (36 chars with dashes), validate it's not being used incorrectly
    if (costCodeVuid.length === 36 && costCodeVuid.includes('-')) {
      // Check if this VUID matches a line's VUID (invalid case)
      const isLineVuid = budgetLines.some(line => line.vuid === costCodeVuid);
      if (isLineVuid) {
        console.error('Invalid cost_code_vuid detected in getCostCodeName - it matches a line VUID:', costCodeVuid);
        return 'Invalid Cost Code';
      }
    }
    
    // First check global cost codes
    const globalCostCode = costCodes.find(c => c.vuid === costCodeVuid);
    if (globalCostCode) {
      // Validate that the code is not a VUID
      if (globalCostCode.code && globalCostCode.code.length === 36 && globalCostCode.code.includes('-')) {
        console.error('Global cost code has VUID as code value:', globalCostCode);
        return 'Invalid Cost Code';
      }
      return `${globalCostCode.code} - ${globalCostCode.description}`;
    }
    
    // If not found in global, check project-specific cost codes
    const projectCostCode = projectCostCodes.find(c => c.vuid === costCodeVuid);
    if (projectCostCode) {
      // Validate that the code is not a VUID
      if (projectCostCode.code && projectCostCode.code.length === 36 && projectCostCode.code.includes('-')) {
        console.error('Project cost code has VUID as code value:', projectCostCode);
        // Try to return just the description if it exists and doesn't contain the VUID
        if (projectCostCode.description && !projectCostCode.description.includes(projectCostCode.code)) {
          return projectCostCode.description;
        }
        return 'Invalid Cost Code';
      }
      return `${projectCostCode.code} - ${projectCostCode.description}`;
    }
    
    return 'Unknown Cost Code';
  };

  const getCostTypeName = (costTypeVuid) => {
    const costType = costTypes.find(c => c.vuid === costTypeVuid);
    return costType ? `${costType.cost_type} (${costType.abbreviation})` : 'Unknown Cost Type';
  };

  const getAccountingPeriodDisplay = (accountingPeriodVuid) => {
    const period = accountingPeriods.find(p => p.vuid === accountingPeriodVuid);
    return period ? `${period.month}/${period.year}` : 'Select Accounting Period';
  };

  // Check if a budget is locked (accounting period is closed)
  const isBudgetLocked = (budget) => {
    if (!budget || !budget.accounting_period_vuid) return false;
    const period = accountingPeriods.find(p => p.vuid === budget.accounting_period_vuid);
    return period && period.status === 'closed';
  };

  const formatCurrency = (amount) => {
    // Ensure we have a valid number
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericAmount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to check if a budget is finalized
  const isBudgetFinalized = (budget) => {
    // Return false if budget is null/undefined (for new budgets)
    if (!budget) {
      return false;
    }
    
    // Check if budget has finalized status from backend
    if (budget.finalized) {
      return true;
    }
    
    // Check localStorage as fallback
    const finalizedBudgets = JSON.parse(localStorage.getItem('finalizedBudgets') || '{}');
    return finalizedBudgets[budget.vuid] === true;
  };

  // Helper function to check if a budget is locked (finalized or accounting period closed)
  const isBudgetLockedOrFinalized = (budget) => {
    return isBudgetFinalized(budget) || isBudgetLocked(budget);
  };

  // Finalization functions
  const handleFinalizeCheckboxChange = (e) => {
    // Use editingBudget if available, otherwise use selectedBudget for newly created budgets
    const budgetToFinalize = editingBudget || selectedBudget;
    if (e.target.checked) {
      setShowFinalizeModal(true);
    } else {
      // If unchecking, just update the form data
      setFormData(prev => ({ ...prev, finalized: false }));
    }
  };

  const handleConfirmFinalize = async () => {
    try {
      // Use editingBudget if available, otherwise use selectedBudget for newly created budgets
      const budgetToFinalize = editingBudget || selectedBudget;
      if (!budgetToFinalize) {
        console.error('No budget to finalize');
        return;
      }
      
      // Store finalized status in localStorage as backup
      const finalizedBudgets = JSON.parse(localStorage.getItem('finalizedBudgets') || '{}');
      finalizedBudgets[budgetToFinalize.vuid] = true;
      localStorage.setItem('finalizedBudgets', JSON.stringify(finalizedBudgets));
      
      // Try to update the backend (but don't fail if it doesn't support finalized field)
      try {
        const baseURL = 'http://localhost:5001';
        await axios.put(`${baseURL}/api/project-budgets/${budgetToFinalize.vuid}`, {
          ...formData,
          finalized: true
        });
        console.log('Backend updated with finalized status');
      } catch (backendError) {
        console.log('Backend may not support finalized field, using local storage fallback');
        // Continue with local finalization even if backend fails
      }
      
      // Close the modal
      setShowFinalizeModal(false);
      
      // Update form data
      setFormData(prev => ({ ...prev, finalized: true }));
      
      // Update the local budgets array to reflect the finalized status
      setBudgets(prevBudgets => 
        prevBudgets.map(budget => 
          budget.vuid === budgetToFinalize.vuid 
            ? { ...budget, finalized: true }
            : budget
        )
      );
      
      // Update the filtered budgets as well
      setFilteredBudgets(prevFiltered => 
        prevFiltered.map(budget => 
          budget.vuid === budgetToFinalize.vuid 
            ? { ...budget, finalized: true }
            : budget
        )
      );
      
      // Update selectedBudget if it's the same budget
      if (selectedBudget && selectedBudget.vuid === budgetToFinalize.vuid) {
        setSelectedBudget(prev => ({ ...prev, finalized: true }));
      }
      
      // Update editingBudget if it exists and matches
      if (editingBudget && editingBudget.vuid === budgetToFinalize.vuid) {
        setEditingBudget(prev => prev ? { ...prev, finalized: true } : null);
      }
      
      // Close the edit form and return to budgets list
      resetForm();
      
      // Also close the budget lines view if it's open
      setSelectedBudget(null);
      setBudgetLines([]);
      setShowLinesForm(false);
      
      // Refresh the budgets data from the backend to ensure consistency
      fetchData();
      
      alert('Budget has been finalized successfully!');
    } catch (error) {
      console.error('Error finalizing budget:', error);
      alert('Error finalizing budget. Please try again.');
    }
  };

  const handleCancelFinalize = () => {
    setShowFinalizeModal(false);
    // Reset the checkbox
    setFormData(prev => ({ ...prev, finalized: false }));
  };

  // Excel-style form functions
  const handleAddNewLine = (fromTab = false) => {
    // Get the budget to use - either selectedBudget or check if we're in create mode
    const budgetToUse = selectedBudget;
    
    // Prevent adding lines to finalized budgets
    if (budgetToUse && isBudgetFinalized(budgetToUse)) {
      alert('This budget is finalized and cannot be modified.');
      return;
    }
    
    // If no budget exists yet, we can't add lines
    if (!budgetToUse) {
      alert('Please create the budget first before adding lines.');
      return;
    }
    
    // If called from Tab key, always create a new line (even if fields are empty)
    // Otherwise, only create if required fields are filled
    if (fromTab || (newLineData.cost_code_vuid && newLineData.cost_type_vuid && newLineData.budget_amount)) {
      const newLine = {
        ...newLineData,
        budget_vuid: budgetToUse.vuid,
        vuid: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` // More unique temporary ID
      };
      
      // If using project-specific cost codes, store the actual code value
      if (useProjectCostCodes && projectSettings?.allow_project_cost_codes) {
        // Check if cost_code_vuid is a VUID (existing cost code) or a code string (new code)
        if (newLineData.cost_code_vuid && newLineData.cost_code_vuid.length === 36 && newLineData.cost_code_vuid.includes('-')) {
          // It's a VUID - use it directly (it's an existing cost code)
          newLine.cost_code_vuid = newLineData.cost_code_vuid;
          newLine._originalCostCode = null; // No need to create, it already exists
        } else {
          // It's a code string - store it for later conversion
          newLine.cost_code_vuid = newLineData.cost_code_vuid;
          newLine._originalCostCode = newLineData.cost_code_vuid;
        }
      }
      
      const updatedLines = [...budgetLines, newLine];
      setBudgetLines(updatedLines);
      
      // Clear the new line form
      setNewLineData({
        cost_code_vuid: '',
        cost_type_vuid: '',
        budget_amount: '',
        notes: ''
      });
      
      // Focus on the first field of the new line
      setTimeout(() => {
        const newLineInput = document.getElementById('new-line-cost-code');
        if (newLineInput) {
          newLineInput.focus();
          // Also scroll to the new line
          newLineInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Handle Tab key to create new line
  const handleTabKey = (e, fieldIndex) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      // Field indices: 0=Cost Code, 1=Cost Type, 2=Budget Amount, 3=Notes
      // Create new line when tabbing from Budget Amount (2) or Notes (3)
      if (fieldIndex === 2 || fieldIndex === 3) {
        e.preventDefault();
        handleAddNewLine(true);
      }
    }
  };

  const handleSaveAllLines = async () => {
    try {
      const baseURL = 'http://localhost:5001';
      let savedCount = 0;
      let updatedCount = 0;
      const savedLineKeys = new Set(); // Track saved lines to prevent duplicates
      
      // Prevent multiple simultaneous saves
      if (saving) {
        console.log('Save already in progress, ignoring duplicate call');
        return;
      }
      setSaving(true);
      
      // First, check if there's data in the new line form that needs to be saved
      // BUT: Only save if it's not already in the budgetLines array
      const formDataKey = `${newLineData.cost_code_vuid}-${newLineData.cost_type_vuid}-${newLineData.budget_amount}`;
      const isFormDataInGrid = budgetLines.some(line => 
        line.cost_code_vuid === newLineData.cost_code_vuid &&
        line.cost_type_vuid === newLineData.cost_type_vuid &&
        line.budget_amount === newLineData.budget_amount &&
        line.vuid.startsWith('temp-')
      );
      
      console.log('New line data validation:', {
        cost_code_vuid: newLineData.cost_code_vuid,
        cost_type_vuid: newLineData.cost_type_vuid,
        budget_amount: newLineData.budget_amount,
        isFormDataInGrid,
        will_save: !!(newLineData.cost_code_vuid && newLineData.cost_type_vuid && newLineData.budget_amount !== undefined && newLineData.budget_amount !== '' && !isFormDataInGrid)
      });
      
      if (newLineData.cost_code_vuid && newLineData.cost_type_vuid && newLineData.budget_amount !== undefined && newLineData.budget_amount !== '' && !isFormDataInGrid) {
        // Handle cost code - it should be a VUID when selected from dropdown (global or project-specific)
        let costCodeVuid = newLineData.cost_code_vuid;
        
        // If it's already a VUID (36 chars with dashes), use it directly
        const isVuid = costCodeVuid && costCodeVuid.length === 36 && costCodeVuid.includes('-');
        
        if (!isVuid && useProjectCostCodes && projectSettings?.allow_project_cost_codes) {
          // Only for project-specific codes: if it's not a VUID, try to create/lookup the cost code
          costCodeVuid = await createProjectCostCodeIfNeeded(newLineData.cost_code_vuid);
        }
        
        // Final validation - costCodeVuid should be a valid VUID at this point
        if (!costCodeVuid || !(costCodeVuid.length === 36 && costCodeVuid.includes('-'))) {
          console.error('ERROR: Invalid costCodeVuid after processing:', costCodeVuid);
          setError('Invalid cost code. Please select a valid cost code from the dropdown.');
          setSaving(false);
          return;
        }

        // Save the new line data first
        await axios.post(`${baseURL}/api/project-budgets/${selectedBudget.vuid}/lines`, {
          budget_vuid: selectedBudget.vuid,
          cost_code_vuid: costCodeVuid,
          cost_type_vuid: newLineData.cost_type_vuid,
          budget_amount: newLineData.budget_amount,
          notes: newLineData.notes || '',
          status: 'active'
        });
        savedCount++;
        savedLineKeys.add(formDataKey);
        
        // Clear the new line form after saving
        setNewLineData({
          cost_code_vuid: '',
          cost_type_vuid: '',
          budget_amount: '',
          notes: ''
        });
      }
      
      // Process all lines in budgetLines - filter out duplicates
      const uniqueLines = [];
      const seenKeys = new Set();
      
      for (const line of budgetLines) {
        const lineKey = `${line.cost_code_vuid}-${line.cost_type_vuid}-${line.budget_amount}`;
        if (!seenKeys.has(lineKey)) {
          seenKeys.add(lineKey);
          uniqueLines.push(line);
        } else {
          console.log('Skipping duplicate line:', lineKey);
        }
      }
      
      // Process unique lines only
      for (const line of uniqueLines) {
        // Skip if this line was already saved from the form
        const lineKey = `${line.cost_code_vuid}-${line.cost_type_vuid}-${line.budget_amount}`;
        if (savedLineKeys.has(lineKey)) {
          console.log('Skipping line already saved from form:', lineKey);
          continue;
        }
        
        // Validate cost_code_vuid - it should not be the line's own vuid
        if (line.cost_code_vuid === line.vuid) {
          console.error('Skipping line with invalid cost_code_vuid (equals line.vuid):', line.vuid);
          setError(`Error: Line has invalid cost code. Please select a valid cost code for the line.`);
          continue; // Skip this line
        }
        
        // Validate that cost_code_vuid exists in available cost codes (only if it's a VUID)
        if (line.cost_code_vuid && line.cost_code_vuid.length === 36 && line.cost_code_vuid.includes('-')) {
          const allCodes = getCostCodesForDisplay();
          const validCostCode = allCodes.find(c => c.vuid === line.cost_code_vuid);
          if (!validCostCode) {
            console.error('Skipping line with cost_code_vuid not found in available codes:', line.cost_code_vuid);
            setError(`Error: Cost code not found. Please select a valid cost code.`);
            continue; // Skip this line
          }
        }
        
        if (line.vuid.startsWith('temp-')) {
          // This is a new line, save it
          let costCodeVuid = line.cost_code_vuid;
          
          // For project-specific cost codes, we need to handle two cases:
          // 1. If cost_code_vuid is a VUID (36 chars), it's already an existing cost code VUID - use it directly
          // 2. If _originalCostCode exists and is NOT a VUID, it's a code string that needs to be created/looked up
          // 3. If cost_code_vuid is a short string (not a VUID), it's a new code that needs to be created
          if (useProjectCostCodes && projectSettings?.allow_project_cost_codes) {
            // If cost_code_vuid is already a VUID (36 chars), use it directly - it's an existing cost code
            const isVuid = line.cost_code_vuid && line.cost_code_vuid.length === 36 && line.cost_code_vuid.includes('-');
            
            if (isVuid) {
              // It's already a VUID, use it directly - no need to create
              costCodeVuid = line.cost_code_vuid;
            } else if (line._originalCostCode) {
              // Check if _originalCostCode is a VUID
              const originalIsVuid = line._originalCostCode.length === 36 && line._originalCostCode.includes('-');
              if (!originalIsVuid) {
                // _originalCostCode is a code string (not a VUID), create/lookup the cost code
                costCodeVuid = await createProjectCostCodeIfNeeded(line._originalCostCode);
              } else {
                // _originalCostCode is a VUID, use it directly
                costCodeVuid = line._originalCostCode;
              }
            } else if (line.cost_code_vuid && !isVuid) {
              // cost_code_vuid is a code string (not a VUID), create/lookup the cost code
              costCodeVuid = await createProjectCostCodeIfNeeded(line.cost_code_vuid);
            } else {
              // Default: use cost_code_vuid as-is
              costCodeVuid = line.cost_code_vuid;
            }
          }

          // Final validation before saving
          if (!costCodeVuid || costCodeVuid === line.vuid) {
            console.error('Invalid costCodeVuid before saving:', { costCodeVuid, line_vuid: line.vuid });
            setError(`Error: Invalid cost code for new line. Please select a valid cost code.`);
            continue;
          }
          
          // Validate that costCodeVuid is a valid VUID (36 chars) - it should be a cost code VUID at this point
          if (costCodeVuid.length !== 36 || !costCodeVuid.includes('-')) {
            console.error('Invalid costCodeVuid format after processing:', { costCodeVuid });
            setError(`Error: Invalid cost code format. Please select a valid cost code from the dropdown.`);
            continue;
          }

          await axios.post(`${baseURL}/api/project-budgets/${selectedBudget.vuid}/lines`, {
            budget_vuid: line.budget_vuid,
            cost_code_vuid: costCodeVuid,
            cost_type_vuid: line.cost_type_vuid,
            budget_amount: line.budget_amount,
            notes: line.notes || '',
            status: line.status
          });
          savedCount++;
          savedLineKeys.add(lineKey);
        } else if (line.vuid && !line.vuid.startsWith('temp-')) {
          // This is an existing line, check if it needs to be updated
          // For now, we'll update all existing lines to ensure consistency
          // In the future, we could add a 'dirty' flag to only update changed lines
          let costCodeVuid = line.cost_code_vuid;
          
          // For existing lines, we should already have VUIDs, so no conversion needed
          // Only convert if _originalCostCode exists and is NOT a VUID (it's a code string)
          if (useProjectCostCodes && projectSettings?.allow_project_cost_codes && line._originalCostCode) {
            // Check if _originalCostCode is a VUID
            const originalIsVuid = line._originalCostCode.length === 36 && line._originalCostCode.includes('-');
            if (!originalIsVuid) {
              // _originalCostCode is a code string (not a VUID), create/lookup the cost code
              costCodeVuid = await createProjectCostCodeIfNeeded(line._originalCostCode);
            }
            // If _originalCostCode is a VUID, use cost_code_vuid directly (it should already be set correctly)
          }

          // Final validation before updating
          if (!costCodeVuid || costCodeVuid === line.vuid) {
            console.error('Invalid costCodeVuid before updating:', { costCodeVuid, line_vuid: line.vuid });
            setError(`Error: Invalid cost code for existing line. Please select a valid cost code.`);
            continue;
          }

          await axios.put(`${baseURL}/api/project-budgets/lines/${line.vuid}`, {
            budget_vuid: line.budget_vuid,
            cost_code_vuid: costCodeVuid,
            cost_type_vuid: line.cost_type_vuid,
            budget_amount: line.budget_amount,
            notes: line.notes || '',
            status: line.status
          });
          updatedCount++;
        }
      }
      
      setSaving(false);
      
      // Fetch budget lines directly from API to ensure we have the latest data
      // Only fetch once to avoid multiple refreshes
      try {
        const budgetResponse = await axios.get(`${baseURL}/api/project-budgets/${selectedBudget.vuid}`);
        if (budgetResponse.data && budgetResponse.data.budget_lines) {
          const linesWithBudgetVuid = budgetResponse.data.budget_lines.map(line => {
            // Validate cost_code_vuid - if it's the line's own VUID or not found in available codes, clear it
            const isInvalid = line.cost_code_vuid === line.vuid || 
                             (line.cost_code_vuid && line.cost_code_vuid.length === 36 && 
                              !getCostCodesForDisplay().find(c => c.vuid === line.cost_code_vuid));
            
            // Also check if backend returned a cost_code object with a VUID as the code
            const hasInvalidCostCode = line.cost_code && line.cost_code.code && 
                                      line.cost_code.code.length === 36 && 
                                      line.cost_code.code.includes('-');
            
            if (isInvalid || hasInvalidCostCode) {
              console.error('Invalid cost_code_vuid detected in saved line:', {
                line_vuid: line.vuid,
                cost_code_vuid: line.cost_code_vuid,
                cost_code_from_backend: line.cost_code,
                cost_code_found: !!getCostCodesForDisplay().find(c => c.vuid === line.cost_code_vuid)
              });
              // Clear invalid cost_code_vuid and cost_code
              return {
                ...line,
                budget_vuid: selectedBudget.vuid,
                cost_code_vuid: '', // Clear invalid value
                cost_code: null // Clear invalid cost_code object
              };
            }
            return {
              ...line,
              budget_vuid: selectedBudget.vuid
            };
          });
          setBudgetLines(linesWithBudgetVuid);
          console.log(`Budget lines refreshed after save:`, linesWithBudgetVuid.length);
        } else {
          // If no lines, set empty array
          setBudgetLines([]);
        }
      } catch (fetchErr) {
        console.error('Error fetching budget lines after save:', fetchErr);
        // Don't call fetchData() here as it might cause refresh loops
        // Just log the error and let the user refresh manually if needed
      }
      setError('');
      
      let message = '';
      if (savedCount > 0 && updatedCount > 0) {
        message = `${savedCount} new line${savedCount > 1 ? 's' : ''} saved and ${updatedCount} existing line${updatedCount > 1 ? 's' : ''} updated successfully!`;
      } else if (savedCount > 0) {
        message = `${savedCount} new budget line${savedCount > 1 ? 's' : ''} saved successfully!`;
      } else if (updatedCount > 0) {
        message = `${updatedCount} existing line${updatedCount > 1 ? 's' : ''} updated successfully!`;
      } else {
        message = 'All budget lines are already saved. No changes to commit.';
      }
      
      setSuccessMessage(message);
    } catch (err) {
      console.error('Error saving budget lines:', err);
      setError('Error saving budget lines: ' + (err.response?.data?.error || err.message));
      setSaving(false);
    }
  };

  // Handle going back to main budget view after adding lines
  const handleDoneAddingLines = () => {
    // Check if there are unsaved temporary lines
    const hasUnsavedLines = budgetLines.some(line => line.vuid.startsWith('temp-'));
    
    if (hasUnsavedLines) {
      if (window.confirm('You have unsaved budget lines. Are you sure you want to discard them?')) {
        setBudgetLines([]);
      } else {
        return; // Don't proceed if user cancels
      }
    }
    
    setShowLinesForm(false);
    setSelectedBudget(null);
    setShowCreateForm(false);
    setEditingBudget(null);
    setSuccessMessage(''); // Clear success message
    // Reset the line form data
    setLineFormData({
      budget_vuid: '',
      cost_code_vuid: '',
      cost_type_vuid: '',
      budget_amount: '',
      notes: '',
      status: 'active'
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-5xl font-bold text-gray-900 mb-4 font-sans">
          Project Budgets
        </h1>
        <p className="text-xl text-gray-700 font-light">
          Create and manage original project budgets. Internal Change Orders are managed separately.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="text-center mb-8">
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleCreateBudget}
            className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
            data-tooltip="Add Budget"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Budget</span>
          </button>
          <button
            onClick={() => window.location.href = '/internal-change-orders'}
            className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
            data-tooltip="Add Internal Change Order"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Internal Change Order</span>
          </button>
          <button
            onClick={handleRetrieveModal}
            className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
            data-tooltip="Download"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Download</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Create/Edit Budget Form - Show header form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            {editingBudget ? 'Edit Budget' : selectedBudget ? 'Budget: ' + formData.description : 'Create New Budget'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Row + 1: Project, Budget Date, Accounting Period */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {/* Project Selection */}
              <div className="dropdown-container relative">
                <label className="block text-xs font-semibold text-gray-900 mb-0.5">Project *</label>
                <input
                  type="text"
                  value={getProjectName(formData.project_vuid)}
                  onClick={() => !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && setProjectDropdownOpen(!projectDropdownOpen)}
                  readOnly
                  className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:border-transparent transition-all ${
                    isBudgetLockedOrFinalized(editingBudget) || selectedBudget
                      ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                      : 'border-gray-300 focus:ring-gray-800'
                  }`}
                  placeholder="Select Project"
                />
                {projectDropdownOpen && !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {projects
                      .filter(project => project.status === 'active')
                      .map((project) => (
                        <div
                          key={project.vuid}
                          onClick={() => {
                            setFormData({...formData, project_vuid: project.vuid});
                            setProjectDropdownOpen(false);
                          }}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{project.project_number}</div>
                          <div className="text-sm text-gray-600">{project.project_name}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Budget Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-900 mb-0.5">Budget Date *</label>
                <input
                  type="date"
                  value={formData.budget_date || new Date().toISOString().split('T')[0]}
                  onChange={(e) => !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && setFormData({...formData, budget_date: e.target.value})}
                  disabled={isBudgetLockedOrFinalized(editingBudget) || selectedBudget}
                  className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:border-transparent transition-all ${
                    isBudgetLockedOrFinalized(editingBudget) || selectedBudget
                      ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                      : 'border-gray-300 focus:ring-gray-800'
                  }`}
                />
              </div>

              {/* Accounting Period Selection */}
              <div className="dropdown-container relative">
                <label className="block text-xs font-semibold text-gray-900 mb-0.5">Accounting Period *</label>
                {(() => {
                  const openPeriods = accountingPeriods.filter(p => p.status === 'open');
                  const hasMultipleOpen = openPeriods.length > 1;
                  
                  if (hasMultipleOpen && !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget) {
                    // Show as select dropdown if multiple open periods
                    return (
                      <select
                        value={formData.accounting_period_vuid}
                        onChange={(e) => setFormData({...formData, accounting_period_vuid: e.target.value})}
                        className="w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:border-transparent transition-all border-gray-300 focus:ring-gray-800"
                      >
                        <option value="">Select Accounting Period</option>
                        {openPeriods.map((period) => (
                          <option key={period.vuid} value={period.vuid}>
                            {period.month}/{period.year}
                          </option>
                        ))}
                      </select>
                    );
                  } else {
                    // Show as input field (for single open period or when editing)
                    // If there's only one open period and no vuid is set, auto-select it
                    if (openPeriods.length === 1 && !formData.accounting_period_vuid && !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget) {
                      // Auto-select the single open period
                      setTimeout(() => {
                        setFormData(prev => ({
                          ...prev,
                          accounting_period_vuid: openPeriods[0].vuid
                        }));
                      }, 0);
                    }
                    
                    return (
                      <>
                        <input
                          type="text"
                          value={getAccountingPeriodDisplay(formData.accounting_period_vuid)}
                          onClick={() => !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && setAccountingPeriodDropdownOpen(!accountingPeriodDropdownOpen)}
                          readOnly
                          className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:border-transparent transition-all ${
                            isBudgetLockedOrFinalized(editingBudget) || selectedBudget
                              ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                              : 'border-gray-300 focus:ring-gray-800'
                          }`}
                          placeholder="Period"
                        />
                        {accountingPeriodDropdownOpen && !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {openPeriods.map((period) => (
                              <div
                                key={period.vuid}
                                onClick={() => {
                                  setFormData({...formData, accounting_period_vuid: period.vuid});
                                  setAccountingPeriodDropdownOpen(false);
                                }}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">{period.month}/{period.year}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  }
                })()}
              </div>
            </div>

            {/* Row 2: Budget Type, Description, Total Amount */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {/* Budget Type */}
              <div className="dropdown-container relative">
                <label className="block text-xs font-semibold text-gray-900 mb-0.5">Budget Type *</label>
                <input
                  type="text"
                  value={formData.budget_type.charAt(0).toUpperCase() + formData.budget_type.slice(1)}
                  onClick={() => !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && setTypeDropdownOpen(!typeDropdownOpen)}
                  readOnly
                  className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:border-transparent transition-all ${
                    isBudgetLockedOrFinalized(editingBudget) || selectedBudget
                      ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                      : 'border-gray-300 focus:ring-gray-800'
                  }`}
                  placeholder="Type"
                />
                {typeDropdownOpen && !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                    {['original', 'revised'].map((type) => (
                      <div
                        key={type}
                        onClick={() => {
                          setFormData({...formData, budget_type: type});
                          setTypeDropdownOpen(false);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-900 mb-0.5">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => !isBudgetLockedOrFinalized(editingBudget) && !selectedBudget && setFormData({...formData, description: e.target.value})}
                  disabled={isBudgetLockedOrFinalized(editingBudget) || selectedBudget}
                  className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:border-transparent transition-all ${
                    isBudgetLockedOrFinalized(editingBudget) || selectedBudget
                      ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                      : 'border-gray-300 focus:ring-gray-800'
                  }`}
                  placeholder="Budget description"
                  maxLength={500}
                />
              </div>

              {/* Total Amount (Calculated) */}
              <div>
                <label className="block text-xs font-semibold text-gray-900 mb-0.5">Total Amount</label>
                <div className="space-y-0.5">
                  <input
                    type="text"
                    value={(() => {
                      if (!selectedBudget) return '$0.00';
                      const total = budgetLines.reduce((sum, line) => sum + (parseFloat(line.budget_amount) || 0), 0);
                      return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      }).format(total);
                    })()}
                    className="w-full px-2 py-1 border rounded text-xs bg-gray-50 text-gray-700 border-gray-300"
                    readOnly
                    placeholder="$0.00"
                  />
                  {selectedBudget && (() => {
                    const originalAmount = budgetLines.reduce((sum, line) => sum + (parseFloat(line.budget_amount) || 0), 0);
                    const currentAmount = getCurrentBudgetAmount(selectedBudget);
                    const hasChangeOrders = currentAmount !== originalAmount;
                    
                    if (hasChangeOrders) {
                      return (
                        <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-xs">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">With change orders:</span>
                            <span className="text-xs font-semibold text-green-600">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                              }).format(currentAmount)}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <p className="text-xs text-gray-500 mt-0.5">
                    Auto-calculated
                  </p>
                </div>
              </div>
            </div>

            {/* Finalize Original Budget */}
            {formData.budget_type === 'original' && !isBudgetLockedOrFinalized(editingBudget || selectedBudget) && (
              <div className="mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="finalize-budget"
                    checked={formData.finalized}
                    onChange={handleFinalizeCheckboxChange}
                    className="w-4 h-4 text-vermillion-600 border-gray-300 rounded focus:ring-vermillion-500 focus:ring-1"
                  />
                  <label htmlFor="finalize-budget" className="text-xs font-semibold text-gray-900">
                    Finalize Original Budget
                  </label>
                </div>
                <p className="text-xs text-gray-600 mt-0.5 ml-6">
                  Once finalized, this budget and its lines cannot be edited
                </p>
              </div>
            )}
            
            {/* Finalized or Locked Budget Warning */}
            {editingBudget && (isBudgetFinalized(editingBudget) || isBudgetLocked(editingBudget)) && (
              <div className="mt-2">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 + 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 + 1.732 2.5z" />
                    </svg>
                    <div>
                      <h3 className="text-lg font-semibold text-red-900">
                        {isBudgetFinalized(editingBudget) ? 'Budget Finalized' : 'Budget Locked'}
                      </h3>
                      <p className="text-red-700 text-sm">
                        {isBudgetFinalized(editingBudget) 
                          ? 'This budget is finalized and cannot be modified. All fields are read-only.'
                          : 'This budget cannot be modified because its accounting period is closed. Create a change order to modify values.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center space-x-4 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                style={{ color: '#7F1D1D' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={selectedBudget && !editingBudget}
                className={`font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg ${
                  selectedBudget && !editingBudget
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={!(selectedBudget && !editingBudget) ? { color: '#7F1D1D' } : {}}
              >
                {editingBudget ? 'Update Budget' : (selectedBudget ? 'Budget Created' : 'Create Budget')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Budget Lines Excel-Style Form - Show when creating new budget OR when viewing existing budget */}
      {(showCreateForm || (showLinesForm && selectedBudget)) && (
        <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Budget Lines Entry - Excel Style
          </h2>
          
          {/* Show message if budget hasn't been created yet */}
          {showCreateForm && !selectedBudget && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-700 font-medium text-center">
                Please fill in the budget header fields above and click "Create Budget" to begin adding budget lines.
              </p>
            </div>
          )}
          
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-600 font-medium">{successMessage}</p>
            </div>
          )}
          
          <div className="mb-6">
            <p className="text-gray-600 text-center">
              Use Tab to navigate between fields, Enter to add a new line, and start typing in dropdowns to search
            </p>
          </div>

          {/* Cost Code Toggle - Only show if project allows project-specific cost codes */}
          {projectSettings?.allow_project_cost_codes && (
            <div className="mb-6 flex items-center justify-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Cost Code Source:</span>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setUseProjectCostCodes(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !useProjectCostCodes
                      ? 'bg-vermillion-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Global
                </button>
                <button
                  type="button"
                  onClick={() => setUseProjectCostCodes(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    useProjectCostCodes
                      ? 'bg-vermillion-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Project-Specific
                </button>
              </div>
              <div className="text-xs text-gray-500">
                {useProjectCostCodes 
                  ? 'Enter custom cost codes manually' 
                  : 'Select from global master data'
                }
              </div>
            </div>
          )}

          {/* Add Item Button - Only show if budget exists */}
          {selectedBudget && (
            <div className="mb-4 text-center">
              <button
                type="button"
                onClick={handleAddNewLine}
                disabled={isBudgetFinalized(selectedBudget)}
                className={`bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg transition-all duration-200 ${
                  isBudgetFinalized(selectedBudget)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:scale-105'
                }`}
                data-tooltip={isBudgetFinalized(selectedBudget) ? 'Budget is finalized and cannot be modified' : 'Add new budget line'}
              >
                Add Item
              </button>
            </div>
          )}

          {/* Excel-style grid */}
          <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
            <table className="min-w-full border border-gray-300" style={{ position: 'relative' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Code *</th>
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Type *</th>
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Budget Amount *</th>
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Notes</th>
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!selectedBudget ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">
                      Create the budget above to begin adding budget lines.
                    </td>
                  </tr>
                ) : (
                  budgetLines.map((line, index) => (
                  <tr key={line.vuid || index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">
                      {/* Both modes use dropdown - project-specific shows project codes, global shows global codes */}
                      <div className="dropdown-container relative">
                        <input
                          type="text"
                          value={(() => {
                            // First, check if backend provided cost_code object with code
                            // BUT: validate that the code is not a VUID (36 chars with dashes)
                            if (line.cost_code && line.cost_code.code) {
                              // If the code looks like a VUID, it's invalid
                              if (line.cost_code.code.length === 36 && line.cost_code.code.includes('-')) {
                                console.error('Backend returned VUID as cost code:', line.cost_code.code);
                                // Clear the invalid value
                                const newLines = [...budgetLines];
                                newLines[index] = { ...newLines[index], cost_code_vuid: '', cost_code: null };
                                setBudgetLines(newLines);
                                return '';
                              }
                              return line.cost_code.code;
                            }
                            
                            // Validate that cost_code_vuid is not the line's own vuid
                            if (line.cost_code_vuid === line.vuid) {
                              console.error('ERROR: cost_code_vuid is set to line.vuid!', { line_vuid: line.vuid, cost_code_vuid: line.cost_code_vuid });
                              // Clear the invalid value
                              const newLines = [...budgetLines];
                              newLines[index] = { ...newLines[index], cost_code_vuid: '' };
                              setBudgetLines(newLines);
                              return '';
                            }
                            
                            // If cost_code_vuid is empty or invalid, return empty
                            if (!line.cost_code_vuid) {
                              return '';
                            }
                            
                            // If cost_code_vuid looks like a VUID (36 chars with dashes), validate it's not the line's VUID
                            if (line.cost_code_vuid.length === 36 && line.cost_code_vuid.includes('-')) {
                              // Check if it's a valid cost code VUID by looking it up
                              const allCodes = getCostCodesForDisplay();
                              const selectedCode = allCodes.find(c => c.vuid === line.cost_code_vuid);
                              
                              if (selectedCode) {
                                return selectedCode.code;
                              }
                              
                              // If not found, it's likely invalid - clear it
                              console.warn('Cost code VUID not found in available codes - likely invalid:', line.cost_code_vuid);
                              const newLines = [...budgetLines];
                              newLines[index] = { ...newLines[index], cost_code_vuid: '' };
                              setBudgetLines(newLines);
                              return '';
                            }
                            
                            // For project-specific codes that might be strings (not VUIDs), return as-is
                            if (useProjectCostCodes && projectSettings?.allow_project_cost_codes) {
                              // Only return if it's not a VUID
                              if (line.cost_code_vuid.length !== 36 || !line.cost_code_vuid.includes('-')) {
                                return line.cost_code_vuid;
                              }
                            }
                            
                            return '';
                          })()}
                          onChange={(e) => {
                            // Prevent manual editing - only allow selection from dropdown
                            // This prevents VUIDs from being entered directly
                          }}
                          onFocus={(e) => {
                            setCostCodeDropdownOpen(index);
                            // Initialize filtered cost codes with all available codes when opening dropdown
                            const allCodes = getCostCodesForDisplay();
                            setFilteredCostCodes(allCodes);
                            // Scroll the input into view so the dropdown is visible
                            setTimeout(() => {
                              e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                          }}
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                          placeholder="Type to search..."
                          readOnly
                        />
                        {costCodeDropdownOpen === index && (
                          <div 
                            ref={(el) => {
                              if (el) {
                                // Scroll dropdown into view when it opens
                                setTimeout(() => {
                                  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                }, 50);
                              }
                            }}
                            className="absolute z-[9999] w-[500px] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto" style={{
                            top: '100%',
                            left: '0',
                            marginTop: '2px'
                          }}>
                            <input
                              type="text"
                              placeholder="Search cost codes..."
                              className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onChange={(e) => {
                                const searchTerm = e.target.value.toLowerCase();
                                const allCodes = getCostCodesForDisplay();
                                const filtered = searchTerm ? allCodes.filter(costCode => 
                                  (costCode.code && costCode.code.toLowerCase().includes(searchTerm) || 
                                   costCode.description && costCode.description.toLowerCase().includes(searchTerm))
                                ) : allCodes;
                                setFilteredCostCodes(filtered);
                              }}
                              onFocus={(e) => {
                                // Initialize with all available cost codes when dropdown opens
                                const allCodes = getCostCodesForDisplay();
                                setFilteredCostCodes(allCodes);
                              }}
                              autoFocus
                            />
                            {(filteredCostCodes.length > 0 ? filteredCostCodes : getCostCodesForDisplay()).map((costCode) => {
                              // Validate that we're not accidentally using the line's vuid
                              if (!costCode.vuid || costCode.vuid === line.vuid) {
                                console.error('Invalid cost code in dropdown:', { costCode, line_vuid: line.vuid });
                                return null;
                              }
                              return (
                                <div
                                  key={costCode.vuid}
                                  onClick={() => {
                                    // Double-check we're setting the cost code vuid, not the line vuid
                                    if (costCode.vuid === line.vuid) {
                                      console.error('ERROR: Attempted to set cost_code_vuid to line.vuid!');
                                      return;
                                    }
                                    const newLines = [...budgetLines];
                                    newLines[index] = { 
                                      ...newLines[index], 
                                      cost_code_vuid: costCode.vuid 
                                    };
                                    // Validate the update
                                    if (newLines[index].cost_code_vuid === newLines[index].vuid) {
                                      console.error('ERROR: After update, cost_code_vuid equals line.vuid!');
                                      return;
                                    }
                                    setBudgetLines(newLines);
                                    setCostCodeDropdownOpen(null);
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900">{costCode.code}</div>
                                  <div className="text-sm text-gray-600">{costCode.description}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="dropdown-container relative">
                        <input
                          type="text"
                          value={costTypes.find(c => c.vuid === line.cost_type_vuid)?.cost_type || line.cost_type_vuid || ''}
                          onChange={(e) => {
                            const newLines = [...budgetLines];
                            newLines[index] = { ...newLines[index], cost_type_vuid: e.target.value };
                            setBudgetLines(newLines);
                          }}
                          onFocus={(e) => {
                            setCostTypeDropdownOpen(index);
                            // Scroll the input into view so the dropdown is visible
                            setTimeout(() => {
                              e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                          }}
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                          placeholder="Type to search..."
                        />
                        {costTypeDropdownOpen === index && (
                          <div 
                            ref={(el) => {
                              if (el) {
                                // Scroll dropdown into view when it opens
                                setTimeout(() => {
                                  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                }, 50);
                              }
                            }}
                            className="absolute z-[9999] w-[500px] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto" style={{
                            top: '100%',
                            left: '0',
                            marginTop: '2px'
                          }}>
                            <input
                              type="text"
                              placeholder="Search cost types..."
                              className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onChange={(e) => {
                                const searchTerm = e.target.value.toLowerCase();
                                const filtered = costTypes.filter(costType => 
                                  costType.status === 'active' && 
                                  (costType.cost_type && costType.cost_type.toLowerCase().includes(searchTerm) || 
                                   costType.description && costType.description.toLowerCase().includes(searchTerm))
                                );
                                setFilteredCostTypes(filtered);
                              }}
                              autoFocus
                            />
                            {(filteredCostTypes.length > 0 ? filteredCostTypes : costTypes.filter(c => c.status === 'active')).map((costType) => (
                              <div
                                key={costType.vuid}
                                onClick={() => {
                                  const newLines = [...budgetLines];
                                  newLines[index] = { ...newLines[index], cost_type_vuid: costType.vuid };
                                  setBudgetLines(newLines);
                                  setCostTypeDropdownOpen(null);
                                }}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">{costType.cost_type} ({costType.abbreviation})</div>
                                <div className="text-sm text-gray-600">{costType.description}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.budget_amount || ''}
                        onChange={(e) => {
                          const newLines = [...budgetLines];
                          newLines[index] = { ...newLines[index], budget_amount: e.target.value };
                          setBudgetLines(newLines);
                        }}
                        className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                        placeholder="0.00"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddNewLine();
                          } else if (e.key === 'Tab' && !e.shiftKey) {
                            // Tab from Budget Amount creates new line
                            e.preventDefault();
                            handleAddNewLine(true);
                          }
                        }}
                      />
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <input
                        type="text"
                        value={line.notes || ''}
                        onChange={(e) => {
                          const newLines = [...budgetLines];
                          newLines[index] = { ...newLines[index], notes: e.target.value };
                          setBudgetLines(newLines);
                        }}
                        className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                        placeholder="Optional notes"
                        maxLength={500}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddNewLine();
                          } else if (e.key === 'Tab' && !e.shiftKey) {
                            // Tab from Notes creates new line
                            e.preventDefault();
                            handleAddNewLine(true);
                          }
                        }}
                      />
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => handleDeleteLine(line.vuid)}
                          className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                          data-tooltip="Delete budget line"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
                {/* New line row - only show if budget exists */}
                {selectedBudget && (
                <tr className="bg-blue-50">
                  <td className="border border-gray-300 px-4 py-2">
                    {/* Both modes use dropdown - project-specific shows project codes, global shows global codes */}
                    <div className="dropdown-container relative">
                      <input
                        type="text"
                        id="new-line-cost-code"
                        value={(() => {
                          const allCodes = getCostCodesForDisplay();
                          const selectedCode = allCodes.find(c => c.vuid === newLineData.cost_code_vuid);
                          return selectedCode ? selectedCode.code : '';
                        })()}
                        onChange={(e) => setNewLineData({...newLineData, cost_code_vuid: e.target.value})}
                        onFocus={(e) => {
                          setNewLineCostCodeDropdownOpen(true);
                          // Initialize filtered cost codes with all available codes when opening dropdown
                          const allCodes = getCostCodesForDisplay();
                          setFilteredCostCodes(allCodes);
                          // Scroll the input into view so the dropdown is visible
                          setTimeout(() => {
                            e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 100);
                        }}
                        className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                        placeholder="Type to search..."
                        readOnly
                      />
                      {newLineCostCodeDropdownOpen && (
                        <div 
                          ref={(el) => {
                            if (el) {
                              // Scroll dropdown into view when it opens
                              setTimeout(() => {
                                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                              }, 50);
                            }
                          }}
                          className="absolute z-[9999] w-[500px] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto" style={{
                          top: '100%',
                          left: '0',
                          marginTop: '2px'
                        }}>
                          <input
                            type="text"
                            placeholder="Search cost codes..."
                            className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={(e) => {
                              const searchTerm = e.target.value.toLowerCase();
                              const allCodes = getCostCodesForDisplay();
                              const filtered = searchTerm ? allCodes.filter(costCode => 
                                (costCode.code && costCode.code.toLowerCase().includes(searchTerm) || 
                                 costCode.description && costCode.description.toLowerCase().includes(searchTerm))
                              ) : allCodes;
                              setFilteredCostCodes(filtered);
                            }}
                            onFocus={(e) => {
                              // Initialize with all available cost codes when dropdown opens
                              const allCodes = getCostCodesForDisplay();
                              setFilteredCostCodes(allCodes);
                            }}
                            autoFocus
                          />
                          {(filteredCostCodes.length > 0 ? filteredCostCodes : getCostCodesForDisplay()).map((costCode) => (
                            <div
                              key={costCode.vuid}
                              onClick={() => {
                                setNewLineData({...newLineData, cost_code_vuid: costCode.vuid});
                                setNewLineCostCodeDropdownOpen(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{costCode.code}</div>
                              <div className="text-sm text-gray-600">{costCode.description}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <div className="dropdown-container relative">
                      <input
                        type="text"
                        id="new-line-cost-type"
                        value={costTypes.find(c => c.vuid === newLineData.cost_type_vuid)?.cost_type || ''}
                        onChange={(e) => setNewLineData({...newLineData, cost_type_vuid: e.target.value})}
                        onFocus={(e) => {
                          setNewLineCostTypeDropdownOpen(true);
                          // Scroll the input into view so the dropdown is visible
                          setTimeout(() => {
                            e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 100);
                        }}
                        className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                        placeholder="Type to search..."
                        readOnly
                      />
                                            {newLineCostTypeDropdownOpen && (
                          <div 
                            ref={(el) => {
                              if (el) {
                                // Scroll dropdown into view when it opens
                                setTimeout(() => {
                                  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                }, 50);
                              }
                            }}
                            className="absolute z-[9999] w-[500px] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto" style={{
                            top: '100%',
                            left: '0',
                            marginTop: '2px'
                          }}>
                          <input
                            type="text"
                            placeholder="Search cost types..."
                            className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={(e) => {
                              const searchTerm = e.target.value.toLowerCase();
                              const filtered = costTypes.filter(costType => 
                                costType.status === 'active' && 
                                (costType.cost_type && costType.cost_type.toLowerCase().includes(searchTerm) || 
                                 costType.description && costType.description.toLowerCase().includes(searchTerm))
                              );
                              setFilteredCostTypes(filtered);
                            }}
                            autoFocus
                          />
                          {(filteredCostTypes.length > 0 ? filteredCostTypes : costTypes.filter(c => c.status === 'active')).map((costType) => (
                            <div
                              key={costType.vuid}
                              onClick={() => {
                                setNewLineData({...newLineData, cost_type_vuid: costType.vuid});
                                setNewLineCostTypeDropdownOpen(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{costType.cost_type} ({costType.abbreviation})</div>
                              <div className="text-sm text-gray-600">{costType.description}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newLineData.budget_amount}
                      onChange={(e) => setNewLineData({...newLineData, budget_amount: e.target.value})}
                      className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                      placeholder="0.00"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNewLine();
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                          // Tab from Budget Amount creates new line
                          e.preventDefault();
                          handleAddNewLine(true);
                        }
                      }}
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="text"
                      value={newLineData.notes}
                      onChange={(e) => setNewLineData({...newLineData, notes: e.target.value})}
                                             className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                      placeholder="Optional notes"
                      maxLength={500}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNewLine();
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                          // Tab from Notes creates new line
                          e.preventDefault();
                          handleAddNewLine(true);
                        }
                      }}
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <button
                      type="button"
                      onClick={handleAddNewLine}
                      disabled={selectedBudget && isBudgetFinalized(selectedBudget)}
                      className={`font-semibold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform ${
                        selectedBudget && isBudgetFinalized(selectedBudget)
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-50 hover:bg-gray-100 hover:scale-105 shadow-lg'
                      }`}
                      data-tooltip={selectedBudget && isBudgetFinalized(selectedBudget) ? 'Budget is finalized and cannot be modified' : 'Add new budget line'}
                    >
                      Add Line
                    </button>
                  </td>
                </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center space-x-6 pt-8">
            <button
              type="button"
              onClick={handleDoneAddingLines}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              Done
            </button>
            <button
              type="button"
              onClick={handleSaveAllLines}
              disabled={saving || (selectedBudget && isBudgetFinalized(selectedBudget))}
              className={`font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-200 transform ${
                saving || (selectedBudget && isBudgetFinalized(selectedBudget))
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white hover:scale-105'
              }`}
              data-tooltip={selectedBudget && isBudgetFinalized(selectedBudget) ? 'Budget is finalized and cannot be modified' : saving ? 'Saving...' : 'Save all budget lines'}
                         >
               {saving ? (
                 <span className="flex items-center">
                   <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                   Saving...
                 </span>
               ) : (
                 'Save All Lines'
               )}
             </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      {!showCreateForm && !showLinesForm && (
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-vermillion-500"
                placeholder="Search descriptions..."
              />
            </div>
            
            <div className="dropdown-container relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Project</label>
              <input
                type="text"
                value={projectFilter === 'all' ? 'All Projects' : getProjectName(projectFilter)}
                onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-vermillion-500"
                placeholder="All Projects"
              />
              {projectDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div
                    onClick={() => {
                      setProjectFilter('all');
                      setProjectDropdownOpen(false);
                    }}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                  >
                    All Projects
                  </div>
                  {projects
                    .filter(project => project.status === 'active')
                    .map((project) => (
                      <div
                        key={project.vuid}
                        onClick={() => {
                          setProjectFilter(project.vuid);
                          setProjectDropdownOpen(false);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{project.project_number}</div>
                        <div className="text-sm text-gray-600">{project.project_name}</div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="dropdown-container relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
              <input
                type="text"
                value={typeFilter === 'all' ? 'All Types' : typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)}
                onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-vermillion-500"
                placeholder="All Types"
              />
              {typeDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                  <div
                    onClick={() => {
                      setTypeFilter('all');
                      setTypeDropdownOpen(false);
                    }}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                  >
                    All Types
                  </div>
                  {['original', 'revised'].map((type) => (
                    <div
                      key={type}
                      onClick={() => {
                        setTypeFilter(type);
                        setTypeDropdownOpen(false);
                      }}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setProjectFilter('all');
                setTypeFilter('all');
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vermillion-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Budgets List */}
      {!showCreateForm && !showLinesForm && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Project Budgets</h2>
          </div>
          
          {/* Warning message if multiple original budgets exist */}
          {(() => {
            const searchParams = new URLSearchParams(location.search);
            const urlProjectVuid = searchParams.get('project');
            if (urlProjectVuid) {
              const originalBudgets = budgets.filter(b => 
                b.project_vuid === urlProjectVuid && 
                b.budget_type === 'original'
              );
              if (originalBudgets.length > 1) {
                return (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75-1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <strong>Warning:</strong> This project has {originalBudgets.length} original budgets. Only one original budget is allowed per project. Please contact an administrator to resolve this issue.
                      </div>
                    </div>
                  </div>
                );
              }
            }
            return null;
          })()}
          
          {filteredBudgets.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No budgets found matching your criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original Budget Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revised Budget Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getCurrentBudgets().map((budget) => (
                    <tr key={budget.vuid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getProjectName(budget.project_vuid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <span>{budget.description}</span>
                          {budget.budget_type === 'revised' && (
                            <span className="text-xs text-green-600 font-medium">
                              (Change Order)
                            </span>
                          )}
                          <IntegrationIndicator 
                            objectVuid={budget.vuid} 
                            objectType="project_budget" 
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          budget.budget_type === 'original' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {budget.budget_type.charAt(0).toUpperCase() + budget.budget_type.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(budget.budget_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        {(() => {
                          if (budget.budget_type === 'revised') {
                            // For revised budgets, show the original budget amount
                            const originalBudget = budgets.find(b => b.vuid === budget.original_budget_vuid);
                            if (originalBudget) {
                              return (
                                <div className="flex flex-col">
                                  <span>
                                    {formatCurrency(getOriginalBudgetAmount(originalBudget.vuid))}
                                  </span>
                                  <span className="text-xs text-gray-500">Original Budget</span>
                                </div>
                              );
                            }
                            return formatCurrency(0);
                          }
                          
                          // For original budgets, show only the original amount
                          const originalAmount = getOriginalBudgetAmount(budget.vuid);
                          
                          return (
                            <div className="flex flex-col">
                              <span>
                                {formatCurrency(originalAmount)}
                              </span>
                              <span className="text-xs text-gray-500">Original Budget</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        {(() => {
                          if (budget.budget_type === 'revised') {
                            // For revised budgets, show the change amount
                            const changeAmount = getChangeAmountForBudget(budget.vuid);
                            return (
                              <div className="flex flex-col">
                                <span className="text-green-600">
                                  {formatCurrency(changeAmount)}
                                </span>
                                <span className="text-xs text-gray-500">Change Amount</span>
                              </div>
                            );
                          }
                          
                          // For original budgets, show the revised amount (original change orders)
                          return (
                            <div className="flex flex-col">
                              <span className="text-blue-600">
                                {formatCurrency(getRevisedBudgetAmount(budget.vuid))}
                              </span>
                              <span className="text-xs text-gray-500">Revised Budget</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => selectedBudget && selectedBudget.vuid === budget.vuid ? handleHideLines() : handleViewLines(budget)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip={selectedBudget && selectedBudget.vuid === budget.vuid ? 'Hide Lines' : 'View Lines'}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleIntegrationModal(budget)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Send to integration"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEditBudget(budget)}
                            disabled={isBudgetFinalized(budget)}
                            className={`text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors ${
                              isBudgetFinalized(budget)
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                            data-tooltip={isBudgetFinalized(budget) ? 'Budget is finalized and cannot be edited' : 'Edit budget'}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteBudget(budget.vuid)}
                            disabled={isBudgetFinalized(budget)}
                            className={`text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors ${
                              isBudgetFinalized(budget)
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                            data-tooltip={isBudgetFinalized(budget) ? 'Budget is finalized and cannot be deleted' : 'Delete budget'}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filteredBudgets.length > budgetsPerPage && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * budgetsPerPage) + 1} to {Math.min(currentPage * budgetsPerPage, filteredBudgets.length)} of {filteredBudgets.length} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= Math.ceil(filteredBudgets.length / budgetsPerPage)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Budget Lines View */}
      {selectedBudget && !showCreateForm && !showLinesForm && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedBudget.budget_type === 'revised' ? 'Change Order Lines' : 'Budget Lines'} for {getProjectName(selectedBudget.project_vuid)} - {selectedBudget.description}
                <IntegrationIndicator 
                  objectVuid={selectedBudget.vuid} 
                  objectType="budget" 
                  className="ml-3"
                />
              </h2>
              <p className="text-sm text-gray-600">
                Type: {selectedBudget.budget_type.charAt(0).toUpperCase() + selectedBudget.budget_type.slice(1)} | 
                Budget Date: {formatDate(selectedBudget.budget_date)}
                {selectedBudget.budget_type === 'revised' && (
                  <span className="ml-2 text-green-600 font-medium">
                    (Internal Change Order)
                  </span>
                )}
              </p>
            </div>
            <div className="flex space-x-3">
              {selectedBudget.budget_type === 'revised' ? (
                <button
                  onClick={() => {
                    // For revised budgets, we don't allow adding lines directly
                    // They should be managed through the Internal Change Order system
                  }}
                  disabled={true}
                  className="flex items-center space-x-2 bg-white font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors opacity-50 cursor-not-allowed"
                  data-tooltip="Change order lines are managed through the Internal Change Order system"
                >
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Manage in ICO</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowLinesForm(true);
                    }}
                    disabled={selectedBudget && isBudgetFinalized(selectedBudget)}
                    className={`flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors ${
                      selectedBudget && isBudgetFinalized(selectedBudget)
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    data-tooltip={selectedBudget && isBudgetFinalized(selectedBudget) ? 'Budget is finalized and cannot be modified' : 'Edit budget lines'}
                  >
                    <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit Lines</span>
                  </button>
                  <button
                    onClick={() => {
                      if (!selectedBudget) {
                        setError('Please select a budget first');
                        return;
                      }
                      handleDownloadTemplate();
                    }}
                    disabled={!selectedBudget || (selectedBudget && isBudgetFinalized(selectedBudget))}
                    className={`flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors ${
                      !selectedBudget || (selectedBudget && isBudgetFinalized(selectedBudget))
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    data-tooltip={!selectedBudget ? 'Please select a budget first' : (selectedBudget && isBudgetFinalized(selectedBudget) ? 'Budget is finalized and cannot be modified' : 'Download CSV template file')}
                  >
                    <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download Template</span>
                  </button>
                  <button
                    onClick={() => {
                      if (!selectedBudget) {
                        setError('Please select a budget first');
                        return;
                      }
                      if (isBudgetFinalized(selectedBudget)) {
                        setError('Budget is finalized and cannot be modified');
                        return;
                      }
                      console.log('Upload CSV button clicked', csvUploadInputRef.current);
                      csvUploadInputRef.current?.click();
                    }}
                    disabled={!selectedBudget || (selectedBudget && isBudgetFinalized(selectedBudget))}
                    className={`flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors ${
                      !selectedBudget || (selectedBudget && isBudgetFinalized(selectedBudget))
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    data-tooltip={!selectedBudget ? 'Please select a budget first' : (selectedBudget && isBudgetFinalized(selectedBudget) ? 'Budget is finalized and cannot be modified' : 'Upload CSV file to create budget lines')}
                  >
                    <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <span>Upload CSV</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* CSV Upload Help Section */}
          {selectedBudget.budget_type !== 'revised' && (
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
              <button
                type="button"
                onClick={() => setShowCsvHelp((prev) => !prev)}
                className="w-full flex items-center justify-between text-left"
                aria-expanded={showCsvHelp}
                aria-controls="csv-upload-help-content"
              >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                  <div>
                  <h3 className="text-sm font-medium text-blue-800">CSV Upload Format</h3>
                    <p className="text-xs text-blue-700">
                      Click to {showCsvHelp ? 'hide' : 'view'} CSV upload instructions
                    </p>
                  </div>
                </div>
                <span className="ml-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  {showCsvHelp ? '-' : '+'}
                </span>
              </button>
              {showCsvHelp && (
                <div
                  id="csv-upload-help-content"
                  className="mt-3 flex items-start space-x-3 text-sm text-blue-700"
                >
                  <div className="flex-shrink-0 h-5 w-5" />
                  <div className="flex-1">
                    <p>Upload a CSV file with the following columns:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li><strong>cost_code</strong> - The cost code (any code when project-specific cost codes are enabled)</li>
                      <li><strong>cost_type</strong> - The cost type abbreviation (e.g., "LAB", "MAT", "EQ") or full name</li>
                      <li><strong>description</strong> - Description for the budget line</li>
                      <li><strong>budget_amount</strong> - Budget amount (must be greater than + 0)</li>
                    </ul>
                    <p className="mt-2 text-xs">
                      <strong>Tip:</strong> Click "Download Template" to get a pre-filled CSV with example cost codes and cost types.
                    </p>
                    <p className="mt-1 text-xs">
                      <strong>Note:</strong> When project-specific cost codes are enabled, you can use any cost code format you want.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}


          {(() => {
            // Simplified: just show all budget lines for this budget
            const linesToShow = selectedBudget.budget_type === 'revised' ? icoLines : budgetLines;
            
            console.log('View Lines Debug - Simplified:', {
              selectedBudgetVuid: selectedBudget.vuid,
              budgetType: selectedBudget.budget_type,
              totalBudgetLines: budgetLines.length,
              linesToShow: linesToShow.length,
              allBudgetLines: budgetLines,
              zeroAmountLines: budgetLines.filter(line => line.budget_amount === 0 || line.budget_amount === '0'),
              budgetAmounts: budgetLines.map(line => ({ vuid: line.vuid, budget_amount: line.budget_amount, type: typeof line.budget_amount }))
            });
            
            const hasLines = linesToShow && linesToShow.length > 0;
            
            if (!hasLines) {
              return (
                <div className="px-6 py-8 text-center text-gray-500">
                  {selectedBudget.budget_type === 'revised' 
                    ? 'No change order lines found for this Internal Change Order.' 
                    : 'No budget lines found for this budget.'}
                </div>
              );
            }
            
            return (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {selectedBudget.budget_type === 'revised' ? 'Change Amount' : 'Budget Amount'}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change Order Impacts</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                      {selectedBudget.budget_type !== 'revised' && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {linesToShow.map((line) => {
                      // Validate and get cost code display
                      let costCodeDisplay = 'Unknown Cost Code';
                      
                      // First, check if backend provided cost_code object
                      if (line.cost_code && line.cost_code.code) {
                        // Validate that the code is not a VUID
                        if (line.cost_code.code.length === 36 && line.cost_code.code.includes('-')) {
                          console.error('Backend returned VUID as cost code:', line.cost_code);
                          costCodeDisplay = 'Invalid Cost Code';
                        } else {
                          costCodeDisplay = `${line.cost_code.code} - ${line.cost_code.description || ''}`;
                        }
                      } else {
                        // Validate cost_code_vuid before looking it up
                        const costCodeVuid = (line.cost_code_vuid === line.vuid || 
                                             (line.cost_code_vuid && line.cost_code_vuid.length === 36 && 
                                              !costCodes.find(c => c.vuid === line.cost_code_vuid) &&
                                              !projectCostCodes.find(c => c.vuid === line.cost_code_vuid))) 
                                           ? null : line.cost_code_vuid;
                        costCodeDisplay = getCostCodeName(costCodeVuid);
                      }
                      
                      return (
                      <tr key={line.vuid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {costCodeDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getCostTypeName(line.cost_type_vuid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {selectedBudget.budget_type === 'revised' 
                          ? formatCurrency(line.change_amount || line.budget_amount)
                          : formatCurrency(line.budget_amount)
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const impacts = getChangeOrderImpacts(line);
                          if (impacts.total === 0) {
                            return <span className="text-gray-400">No changes</span>;
                          }
                          
                          return (
                            <div className="space-y-1">
                              {impacts.external > 0 && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-blue-600 font-medium">External:</span>
                                  <span className="text-xs text-blue-600">
                                    +{formatCurrency(impacts.external)}
                                  </span>
                                </div>
                              )}
                              {impacts.internal > 0 && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-green-600 font-medium">Internal:</span>
                                  <span className="text-xs text-green-600">
                                    +{formatCurrency(impacts.internal)}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2 border-t pt-1">
                                <span className="text-xs font-semibold text-gray-700">Total:</span>
                                <span className="text-xs font-semibold text-gray-700">
                                  +{formatCurrency(impacts.total)}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {line.notes || line.description || '-'}
                      </td>
                      {selectedBudget.budget_type !== 'revised' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">

                            <button
                              onClick={() => handleDeleteLine(line.vuid)}
                              disabled={selectedBudget && isBudgetFinalized(selectedBudget)}
                              className={`text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors ${
                                selectedBudget && isBudgetFinalized(selectedBudget)
                                  ? 'opacity-50 cursor-not-allowed'
                                  : ''
                              }`}
                              data-tooltip={selectedBudget && isBudgetFinalized(selectedBudget) ? 'Budget is finalized and cannot be deleted' : 'Delete budget line'}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                    );
                    })}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>
      )}

      {/* Integration Modal */}
      {showIntegrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Send Budget to Integration
              </h2>
              <button
                onClick={() => {
                  setShowIntegrationModal(false);
                  setBudgetForIntegration(null);
                  setSelectedIntegration(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Budget Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Budget Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Description:</span>
                  <p className="text-gray-900">{budgetForIntegration?.description}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Project:</span>
                  <p className="text-gray-900">{budgetForIntegration ? getProjectName(budgetForIntegration.project_vuid) : ''}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Type:</span>
                  <p className="text-gray-900 capitalize">{budgetForIntegration?.budget_type}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Status:</span>
                  <p className="text-gray-900 capitalize">{budgetForIntegration?.status}</p>
                </div>
              </div>
            </div>

            {/* Integration Selection */}
            <div className="mb-6">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Select Integration *
              </label>
              {integrations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <p className="text-lg">No integrations available</p>
                  <p className="text-sm">Create an integration first to send budgets to external systems.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {integrations.map((integration) => (
                    <div
                      key={integration.vuid}
                      onClick={() => setSelectedIntegration(integration)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedIntegration?.vuid === integration.vuid
                          ? 'border-gray-800 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                          <p className="text-sm text-gray-600 capitalize">{integration.integration_type.replace('_', ' ')}</p>
                          {integration.base_url && (
                            <p className="text-xs text-gray-500 mt-1">{integration.base_url}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            integration.status === 'active' ? 'bg-green-100 text-green-800' : 
                            integration.status === 'inactive' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {integration.status}
                          </span>
                          {selectedIntegration?.vuid === integration.vuid && (
                            <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowIntegrationModal(false);
                  setBudgetForIntegration(null);
                  setSelectedIntegration(null);
                }}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSendToIntegration}
                disabled={!selectedIntegration || selectedIntegration.integration_type !== 'procore'}
                className={`px-6 py-3 font-semibold rounded-lg transition-all ${
                  selectedIntegration && selectedIntegration.integration_type === 'procore'
                    ? 'bg-gray-50 hover:bg-gray-100'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                style={selectedIntegration && selectedIntegration.integration_type === 'procore' ? { color: '#7F1D1D' } : {}}
              >
                {selectedIntegration?.integration_type === 'procore' ? 'Send to Procore' : 'Send to Integration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Sending Budget to Procore
              </h2>
              {progressComplete && (
                <button
                  onClick={() => {
                    setShowProgressModal(false);
                    setShowIntegrationModal(false);
                    setBudgetForIntegration(null);
                    setSelectedIntegration(null);
                    fetchData();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Progress Steps */}
            <div className="space-y-4 mb-6">
              {progressSteps.map((step, index) => (
                <div
                  key={step.id}
                  className={`border-2 rounded-lg p-4 transition-all ${
                    step.status === 'completed'
                      ? 'border-green-500 bg-green-50'
                      : step.status === 'error'
                      ? 'border-red-500 bg-red-50'
                      : step.status === 'in_progress'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Status Icon */}
                      {step.status === 'completed' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      {step.status === 'error' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                      {step.status === 'in_progress' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                      {step.status === 'pending' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-semibold">{index + 1}</span>
                        </div>
                      )}

                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{step.name}</h3>
                        {step.details && (
                          <p className="text-sm text-gray-600 mt-1">{step.details}</p>
                        )}
                        {step.total > 0 && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all ${
                                  step.status === 'completed' ? 'bg-green-500' : 
                                  step.status === 'error' ? 'bg-red-500' : 
                                  'bg-blue-500'
                                }`}
                                style={{ width: `${Math.min((step.count / step.total) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {step.count} / {step.total}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Errors */}
            {progressErrors.length > 0 && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-red-900 mb-2">Errors:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {progressErrors.map((error, idx) => (
                    <li key={idx} className="text-sm text-red-700">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Success Message */}
            {progressComplete && progressErrors.length === 0 && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-semibold text-green-900">
                    Budget successfully sent to Procore!
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              {progressComplete ? (
                <button
                  onClick={() => {
                    setShowProgressModal(false);
                    setShowIntegrationModal(false);
                    setBudgetForIntegration(null);
                    setSelectedIntegration(null);
                    fetchData();
                  }}
                  className="px-6 py-3 font-semibold rounded-lg transition-all bg-gray-50 hover:bg-gray-100"
                  style={{ color: '#7F1D1D' }}
                >
                  Close
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowProgressModal(false);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Retrieve Budgets Modal */}
      {showRetrieveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Retrieve Budgets from Integration
              </h2>
              <button
                onClick={() => {
                  setShowRetrieveModal(false);
                  setRetrieveIntegration(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Description */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-1">How it works</h3>
                  <p className="text-blue-800 text-sm">
                    Select an integration to retrieve budgets from external systems. This will import budget data 
                    and create new budget records in your system.
                  </p>
                </div>
              </div>
            </div>

            {/* Integration Selection */}
            <div className="mb-6">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Select Integration *
              </label>
              {integrations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <p className="text-lg">No integrations available</p>
                  <p className="text-sm">Create an integration first to retrieve budgets from external systems.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {integrations.map((integration) => (
                    <div
                      key={integration.vuid}
                      onClick={() => setRetrieveIntegration(integration)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        retrieveIntegration?.vuid === integration.vuid
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                          <p className="text-sm text-gray-600 capitalize">{integration.integration_type.replace('_', ' ')}</p>
                          {integration.base_url && (
                            <p className="text-xs text-gray-500 mt-1">{integration.base_url}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            integration.status === 'active' ? 'bg-green-100 text-green-800' : 
                            integration.status === 'inactive' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {integration.status}
                          </span>
                          {retrieveIntegration?.vuid === integration.vuid && (
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowRetrieveModal(false);
                  setRetrieveIntegration(null);
                }}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRetrieveBudgets}
                disabled={!retrieveIntegration}
                className={`px-6 py-3 font-semibold rounded-lg transition-all ${
                  retrieveIntegration
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Retrieve Budgets
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Retrieved Budgets Modal */}
      {showRetrievedBudgetsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Review Retrieved Budget Lines
              </h2>
              <button
                onClick={() => {
                  setShowRetrievedBudgetsModal(false);
                  setRetrievedBudgetsData([]);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Description */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                                      <div>
                        <h3 className="text-lg font-semibold text-green-900 mb-1">Locked Budget Lines Retrieved Successfully</h3>
                        <p className="text-green-800 text-sm">
                          Only locked (finalized) budgets from Procore are shown below. Review the budget lines and select the ones you want to import. Use the checkboxes to choose
                          individual lines or use the Select All/Deselect All buttons. A new budget will be created for the
                          selected project with these budget lines.
                        </p>
                      </div>
              </div>
            </div>

            {/* Selection Controls */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleSelectAllBudgets}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50 transition-all"
                >
                  Select All
                </button>
                <button
                  onClick={handleDeselectAllBudgets}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-all"
                >
                  Deselect All
                </button>
              </div>
              <div className="text-sm text-gray-600">
                {selectedBudgetsToImport.length} of {retrievedBudgetsData.length} budget lines selected
              </div>
            </div>

            {/* Budget Lines Table */}
            <div className="mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedBudgetsToImport.length === retrievedBudgetsData.filter(b => !b.already_imported).length && retrievedBudgetsData.filter(b => !b.already_imported).length > 0}
                          onChange={() => {
                            const newBudgets = retrievedBudgetsData.filter(b => !b.already_imported);
                            if (selectedBudgetsToImport.length === newBudgets.length) {
                              handleDeselectAllBudgets();
                            } else {
                              handleSelectAllBudgets();
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Cost Code
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Cost Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Budget Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Division
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {retrievedBudgetsData.map((budgetLine, index) => {
                      const isAlreadyImported = budgetLine.already_imported || false;
                      return (
                        <tr key={index} className={isAlreadyImported ? "bg-gray-100 hover:bg-gray-150" : "hover:bg-gray-50"}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                              checked={selectedBudgetsToImport.includes(budgetLine) && !isAlreadyImported}
                              disabled={isAlreadyImported}
                            onChange={(e) => {
                                if (!isAlreadyImported) {
                              if (e.target.checked) {
                                setSelectedBudgetsToImport([...selectedBudgetsToImport, budgetLine]);
                              } else {
                                setSelectedBudgetsToImport(selectedBudgetsToImport.filter(item => item !== budgetLine));
                                  }
                              }
                            }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                              <div className="font-medium">{budgetLine.cost_code_obj?.code || budgetLine.cost_code || 'N/A'}</div>
                              <div className="text-sm text-gray-500">{budgetLine.cost_code_obj?.description || budgetLine.cost_code_description || ''}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                              <div className="font-medium">{budgetLine.cost_type_obj?.name || budgetLine.cost_type || 'N/A'}</div>
                              <div className="text-sm text-gray-500">({budgetLine.cost_type_obj?.abbreviation || budgetLine.cost_type_abbreviation || ''})</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {formatCurrency(budgetLine.budget_amount)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {budgetLine.division}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {budgetLine.category}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {budgetLine.notes || '-'}
                        </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            {isAlreadyImported ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Already Imported
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                New
                              </span>
                            )}
                          </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowRetrievedBudgetsModal(false);
                  setRetrievedBudgetsData([]);
                }}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleImportBudgets}
                disabled={selectedBudgetsToImport.length === 0}
                className={`px-6 py-3 font-semibold rounded-lg transition-all ${
                  selectedBudgetsToImport.length > 0
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Import Selected Budget Lines
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Budget Confirmation Modal */}
      {showFinalizeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Finalize Original Budget
              </h2>
              <button
                onClick={handleCancelFinalize}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Warning Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-start space-x-3">
                <svg className="w-8 h-8 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 + 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 + 1.732 2.5z" />
                </svg>
                <div>
                  <h3 className="text-xl font-semibold text-red-900 mb-2">⚠️ Finalization Warning</h3>
                  <p className="text-red-800 text-lg">
                    Are you sure you want to finalize your original budget?
                  </p>
                  <p className="text-red-700 text-sm mt-2">
                    Once finalized, this budget and all its budget lines will be locked and cannot be:
                  </p>
                  <ul className="text-red-700 text-sm mt-2 list-disc list-inside space-y-1">
                    <li>Edited or modified</li>
                    <li>Have new budget lines added</li>
                    <li>Have existing budget lines deleted</li>
                    <li>Be unfinalized (this action is permanent)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCancelFinalize}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFinalize}
                className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all"
              >
                Yes, Finalize Budget
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden file input for CSV upload */}
      <input
        ref={csvUploadInputRef}
        type="file"
        accept=".csv,.xlsx"
        style={{ display: 'none' }}
        onChange={handleCsvUpload}
      />
    </div>
  );
};

export default ProjectBudgets;
